/**
 * In-memory mock transport — the offline demo backend.
 *
 * Selected when no `VITE_API_BASE_URL` is configured. It implements every admin route
 * the console calls, holds mutable state (toggling a flag, reordering providers,
 * suspending a user, resolving a KYC or gift-card review all persist for the tab
 * session), and returns the same `{ status, ok, json }` envelope as the real transport —
 * including typed error envelopes for bad credentials or a missing bearer token. It ships
 * so the operator console is always demoable, even in a build that points at a real API.
 */
import type {
  AdminAssetRail,
  AdminBank,
  AdminFee,
  AdminBanner,
  AdminGiftcardBrand,
  AdminLegalDocument,
  AdminRateRow,
  AdminRedemptionDetail,
  AdminThreadRow,
  Attachment,
  BrandAsset,
  BroadcastAudience,
  BroadcastCategory,
  BroadcastPreviewResponse,
  CreateGiftcardBrandRequest,
  CredentialSource,
  EmailBroadcast,
  EmailBroadcastDetail,
  EmailImage,
  EmailImageMime,
  EmailLayout,
  EmailLayoutContent,
  EmailLayoutVersion,
  EmailLink,
  EmailPaletteField,
  EmailTemplate,
  KycTier,
  Message,
  MoneyDTO,
  PlatformSettings,
  ProviderStatus,
  ProviderStatusResponse,
  ReconciliationResponse,
  RenderPreviewResponse,
  SetPlatformSettingsRequest,
  SmtpTestResponse,
  SplashAsset,
  SplashAssetMime,
  SupportSettings,
  TierLimit,
  UpdateGiftcardBrandRequest,
} from '../../contracts/types.js';
/*
 * The provisional half: the widened user/transaction/thread rows, the bill-service catalogue,
 * and the balance-adjustment shapes. Implemented here rather than left out, because a route
 * the console calls and the demo backend does not answer is a demo build that 404s on the
 * pages this work order added — which is precisely the half of the console nobody can check
 * against a real API yet.
 */
import type {
  AdminAdjustBalanceResponse,
  BillService,
  OperatorSettableState,
  AdminUserDetail,
  AdminTreasuryOrder,
  TreasuryOverviewResponse,
  TreasuryThresholds,
  TreasuryRebalance,
  RecordRebalanceRequest,
  ReconciliationScope,
} from '../../contracts/types.js';
/*
 * The RBAC the mock enforces comes from the console's own mirror of `ROLE_PERMISSIONS`, not
 * from a second list of role names written out here. The mock's job is to reproduce the API's
 * 403s so a permission-less role is *seen* to be refused in the demo, and two copies of the
 * grant table would eventually disagree about which one that is.
 */
import { adminCan } from '../../contracts/permissions.js';
import {
  BROADCAST_CATEGORIES,
  EMAIL_IMAGE_MIMES,
  KYC_TIERS,
} from '../../contracts/vocab.js';
import { EMAIL_IMAGE_MAX_BYTES } from '../../lib/emailAttachments.js';
/*
 * The palette rule, the link rule and the layout renderer, taken from the console's own
 * mirrors rather than reimplemented here.
 *
 * `lib/emailLayout.ts` already mirrors `email-brand.ts` and the API's `renderWithLayout`, and
 * `emailLayout.test.ts` pins that mirror against the contract by source path. A third copy of
 * "is this readable" inside the demo backend would be a third answer, and the drift between
 * three answers is invisible until an operator saves a palette the mock accepted and the real
 * API refuses.
 */
import {
  EMAIL_LOGO_MAX_WIDTH,
  EMAIL_LOGO_MIN_WIDTH,
  PALETTE_FIELDS,
  checkEmailPalette,
  emailTokens,
  normaliseEmailLinks,
  renderWithLayout,
} from '../../lib/emailLayout.js';
import { htmlToPlainText } from '../../lib/emailText.js';
import type { RawMediaResponse, RawRequest, RawResponse, Transport } from '../transport.js';
import {
  SAMPLE_VARS,
  seedEmailBroadcasts,
  seedEmailImages,
  seedEmailLayout,
  seedEmailTemplates,
  seedPlatformSettings,
} from './email-defaults.js';
import {
  DEMO_PASSWORD,
  MOCK_ADMINS,
  MOCK_PROVIDER_CATALOG,
  MOCK_PROVIDER_DEFAULT_MODE,
  MOCK_PROVIDER_ENV,
  MOCK_PROVIDER_ENVIRONMENT,
  MOCK_PROVIDER_SERVICES,
  MOCK_RATE_MAX_AGE_SECONDS,
  BILL_ICONS,
  KYC_DOCUMENTS,
  USER_PHOTOS,
  seedAssetRails,
  seedBanners,
  seedBillServices,
  seedBanks,
  seedBrand,
  seedFlags,
  seedGiftcardBrands,
  seedGiftcardRedemptions,
  seedKycQueue,
  seedLegalDocuments,
  seedProviderCredentials,
  seedProviders,
  seedRateBoard,
  seedReconciliation,
  seedSplash,
  seedSupportSettings,
  seedSupportThreads,
  seedTierLimits,
  seedTransactions,
  seedUsers,
  SUPPORT_MEDIA,
  type MockMessage,
  type MockProviderDefinition,
  type MockSupportThread,
} from './data.js';
import {
  rateBetweenDTO,
  seedReconciliationDeltas,
  seedReconciliationRuns,
  seedTreasuryHoldings,
  seedTreasuryOrders,
  seedTreasuryRebalances,
  seedTreasuryThresholds,
} from './treasury-data.js';

const TOKEN_PREFIX = 'genex-mock.';

function ok(json: unknown, status = 200): RawResponse {
  return { status, ok: true, json };
}
function fail(status: number, code: string, message: string): RawResponse {
  return { status, ok: false, json: { error: { code, message, requestId: 'mock-req' } } };
}

/**
 * The fee board, as a fresh deployment sees it: nothing overridden, every knob reading
 * through to its fallback.
 *
 * Seeded with no overrides on purpose. The demo's job on this page is to teach that an
 * untouched knob keeps tracking what it falls back to — the environment for most of them, the
 * manual conversion pair for instant settlement — and a seed with values already typed in
 * would teach the opposite of the thing the page exists to make visible.
 *
 * The labels and descriptions mirror the API's own catalogue rather than being invented
 * here; the real console renders whatever the server sends, and a demo that reads
 * differently from production is a demo nobody trusts.
 */
function seedFees(): AdminFee[] {
  const knob = (
    key: AdminFee['key'],
    label: string,
    description: string,
    envVar: string,
    envValue: string,
    maxValue: string,
    unit: AdminFee['unit'] = 'bps',
  ): AdminFee => ({
    key,
    label,
    description,
    unit,
    inheritsFrom: null,
    envVar,
    envValue,
    overrideValue: null,
    effectiveValue: envValue,
    source: 'env',
    maxValue,
    updatedByAdminId: null,
    updatedAt: null,
  });

  const tracking = (
    key: AdminFee['key'],
    label: string,
    description: string,
    inheritsFrom: AdminFee['key'],
    effectiveValue: string,
  ): AdminFee => ({
    key,
    label,
    description,
    unit: 'bps',
    inheritsFrom,
    envVar: null,
    envValue: null,
    overrideValue: null,
    effectiveValue,
    source: 'inherited',
    maxValue: '1000',
    updatedByAdminId: null,
    updatedAt: null,
  });

  return [
    knob(
      'spread_bps',
      'House spread',
      'Widens the mid-market rate against the customer on every sell, swap and conversion. This is the margin taken through the rate itself rather than as a separate line, so a customer never sees it as a fee. That is why it is capped at 10%.',
      'SPREAD_BPS',
      '0',
      '1000',
    ),
    knob(
      'swap_fee_bps',
      'Swap fee',
      'Charged on a crypto-to-crypto swap, on top of the spread. A sell carries no fee of its own. The spread is the revenue there, and charging both would be charging twice for one trade.',
      'SWAP_FEE_BPS',
      '10',
      '1000',
    ),
    knob(
      'conversion_fee_bps',
      'Conversion fee',
      'Charged on a manual currency conversion, taken out of the leg the customer receives. With the spread at zero this is the entire margin on a conversion.',
      'CONVERSION_FEE_BPS',
      '25',
      '1000',
    ),
    knob(
      'crypto_withdrawal_fee_bps',
      'Crypto withdrawal fee',
      'Charged on a send-out, in the coin being sent, to cover the network fee. A coarse proportional stand-in for a per-chain flat cost; zero is a real setting, and means the house absorbs the gas.',
      'CRYPTO_WITHDRAWAL_FEE_BPS',
      '0',
      '5000',
    ),
    knob(
      'bill_fee_bps',
      'Bill convenience fee',
      'Charged on top of every bill payment and auto-refill, in naira. Proportional rather than flat, so a ₦200 airtime top-up is not swamped by it. Capped, below, so a ₦400,000 school fee is not taxed as though the work scaled.',
      'BILL_FEE_BPS',
      '50',
      '1000',
    ),
    knob(
      'bill_fee_cap_kobo',
      'Bill fee cap',
      'The most the bill convenience fee may ever reach on one payment, in kobo. Settable alongside the rate rather than fixed in code: a rate an operator can raise into a cap they cannot move is a knob that stops working silently.',
      'BILL_FEE_CAP_KOBO',
      '10000',
      '10000000',
      'kobo',
    ),
    tracking(
      'instant_settlement_fee_bps',
      'Instant settlement fee',
      'Charged on an instant settlement. Unset, it tracks the manual conversion fee live, so changing that changes this too and the two cannot drift apart unnoticed. Set it only to price instant settlement deliberately differently.',
      'conversion_fee_bps',
      '25',
    ),
    tracking(
      'instant_settlement_spread_bps',
      'Instant settlement spread',
      'The spread applied to an instant settlement. Unset, it tracks the house spread live. Set it only when instant settlement is meant to carry a wider margin than a manual conversion.',
      'spread_bps',
      '0',
    ),
  ];
}

export class MockTransport implements Transport {
  private flags = seedFlags();
  private providers = seedProviders();
  private providerCreds = seedProviderCredentials();
  private rates = seedRateBoard();
  private users = seedUsers();
  private kyc = seedKycQueue();
  private transactions = seedTransactions();
  private recon = seedReconciliation();
  private brand: BrandAsset | null = seedBrand();
  private splash: SplashAsset | null = seedSplash();
  private tierLimits = seedTierLimits();
  private assetRails = seedAssetRails();
  private treasuryConfig = seedTreasuryThresholds();
  private treasuryOrders = seedTreasuryOrders();
  private treasuryRebalances = seedTreasuryRebalances();
  private reconciliationRuns = seedReconciliationRuns();
  private reconciliationDeltas = seedReconciliationDeltas();
  private fees: AdminFee[] = seedFees();
  private banks = seedBanks();
  private billServices: BillService[] = seedBillServices();
  /**
   * Balance adjustments already honoured, keyed by idempotency key.
   *
   * Held rather than discarded because idempotency is the whole safety property of that
   * endpoint: a replayed key must return the first receipt rather than move money again, and a
   * demo backend that did not reproduce it would let a double-submitting page look correct.
   */
  private balanceAdjustments: Record<string, AdminAdjustBalanceResponse> = {};
  private adjustmentSeq = 1;
  /**
   * The live remember-me lineage, and the tokens already spent out of it.
   *
   * Two maps rather than one, because "never issued" and "issued and already used" are
   * different answers: the first is an unknown token, the second is a replay, and only the
   * second burns the lineage.
   */
  private rememberTokens: Record<string, { adminId: string; expiresAt: string }> = {};
  private spentRememberTokens = new Map<string, string>();
  private rememberSeq = 1;
  private banners = seedBanners();
  private bannerSeq = 100;
  private giftcardBrands: AdminGiftcardBrand[] = seedGiftcardBrands();
  private brandSeq = 100;
  private redemptions = seedGiftcardRedemptions();
  private lastRunAt = new Date().toISOString();
  private supportThreads = seedSupportThreads();
  private supportSettings = seedSupportSettings();
  private supportMedia: Record<string, { mime: string; name: string; dataBase64: string }> = {
    ...SUPPORT_MEDIA,
  };
  private supportSeq = 900000;
  private platformSettings = seedPlatformSettings();
  private emailTemplates = seedEmailTemplates();
  private legalDocuments = seedLegalDocuments();
  // ── The email platform's own state ──
  // Held here so the layout editor, the image library and the broadcast composer are all
  // clickable offline, exactly as every other surface in this console is.
  //
  // The layout is a *stack*, not a row: the API's writes are append-only, so a save inserts a
  // version and moves the active marker rather than overwriting. The last entry is always the
  // active one, including after a restore, because restore copies an old version forward as a
  // new one rather than rewinding the pointer. Modelling it as a single mutable row would make
  // the version list and the restore button demo something the server does not do.
  private emailLayouts: EmailLayout[] = [seedEmailLayout()];
  private emailImages: EmailImage[] = seedEmailImages();
  private emailBroadcasts: EmailBroadcastDetail[] = seedEmailBroadcasts();
  // One counter for both, so two ids minted in the same millisecond cannot collide. Counted
  // rather than randomised: the demo has to be the same on every reload.
  private emailIdSeq = 1;

  async send(req: RawRequest): Promise<RawResponse> {
    await new Promise((r) => setTimeout(r, 220 + Math.random() * 260));
    const { method, path } = req;

    // ── login and refresh are the two unauthenticated routes ──
    // Refresh has to be unauthenticated for the reason it exists: the access token has expired,
    // which is precisely why the caller is here. Its credential is the remember-me token in the
    // body, and that token is single-use.
    if (method === 'POST' && path === '/admin/auth/login') return this.login(req.body);
    if (method === 'POST' && path === '/admin/auth/refresh') return this.refresh(req.body);

    // ── everything else requires a valid mock bearer token ──
    const admin = this.adminFromToken(req.token);
    if (admin === null) return fail(401, 'UNAUTHENTICATED', 'Please sign in again.');

    if (method === 'POST' && path === '/admin/auth/logout') {
      // Idempotent, and it burns the lineage rather than only this token: signing out on a
      // shared machine has to mean the remembered session cannot be revived from it.
      for (const [token, entry] of Object.entries(this.rememberTokens)) {
        if (entry.adminId === admin.id) delete this.rememberTokens[token];
      }
      return ok(null, 204);
    }

    if (method === 'GET' && path === '/admin/auth/me') {
      const { password: _pw, ...me } = admin;
      return ok(me);
    }

    // ── flags ──
    if (method === 'GET' && path === '/admin/flags') return ok({ flags: this.flags });
    if (method === 'PUT' && path.startsWith('/admin/flags/')) return this.setFlag(path, req.body, admin.id);

    // ── providers ──
    if (method === 'GET' && path === '/admin/providers') return ok({ providers: this.providers });
    if (method === 'PUT' && path.startsWith('/admin/providers/'))
      return this.setProvider(path, req.body, admin.id);

    // ── provider credentials & activation (a different route from routing, above) ──
    if (method === 'GET' && path === '/admin/provider-credentials')
      return ok(this.providerStatusResponse());
    if (method === 'PUT' && path.startsWith('/admin/provider-credentials/'))
      return this.setProviderCredentials(path, req.body, admin.id, admin.role);

    // ── live rates + operator override ──
    if (method === 'GET' && path === '/admin/rates') return ok(this.rateBoard());
    if (method === 'POST' && path === '/admin/rates/reset') return this.resetAllRates(admin.role);
    if (method === 'PUT' && path.startsWith('/admin/rates/'))
      return this.setRateOverride(path, req.body, admin.id, admin.role);
    if (method === 'DELETE' && path.startsWith('/admin/rates/'))
      return this.clearRateOverride(path, admin.role);

    // ── users ──
    if (method === 'GET' && path === '/admin/users') return this.listUsers(req.query?.['q']);
    if (method === 'POST' && path.endsWith('/suspend')) return this.suspendUser(path, req.body);
    if (method === 'POST' && path.endsWith('/unsuspend')) return this.unsuspendUser(path);
    /*
     * The separate account states, and the two money movements.
     *
     * Matched before the bare `GET /admin/users/:id` below, and matched on the method as well
     * as the suffix, so a customer id that happened to end in `credit` could not be routed
     * into a balance change. Every one of them is gated the way the real API will be: the
     * state changes on `users:write` (compliance, superadmin), the money on `rates:write`
     * (finance, superadmin). A support role gets a 403 out of this mock exactly as it would
     * out of the API, which is the point of the mock reproducing RBAC at all.
     */
    if (method === 'POST' && path.startsWith('/admin/users/')) {
      if (path.endsWith('/state')) return this.setAccountState(path, req.body, admin.role);
      if (path.endsWith('/balance')) return this.adjustBalance(path, req.body, admin.role);
    }
    if (method === 'GET' && path.startsWith('/admin/users/')) return this.getUser(path);

    // ── bill services (read on `providers:read`, writes on `rates:write`) ──
    if (method === 'GET' && path === '/admin/bills/services')
      return ok({ services: this.billServices });
    if (method === 'PUT' && path.startsWith('/admin/bills/services/') && path.endsWith('/icon'))
      return this.uploadBillIcon(path, req.body, admin.role);
    if (method === 'PATCH' && path.startsWith('/admin/bills/services/'))
      return this.patchBillService(path, req.body, admin.role);

    // ── KYC review ──
    // `/admin/kyc/pending` is matched before the queue prefix below: it is a sibling path,
    // not a queue item, and a `startsWith('/admin/kyc/')` ordering slip would serve it as
    // one and 404 the watchdog the dashboard polls.
    if (method === 'GET' && path === '/admin/kyc/pending') return ok(this.kycPending());
    if (method === 'GET' && path === '/admin/kyc/queue') return ok({ items: this.kyc, nextCursor: null });
    if (method === 'POST' && path.startsWith('/admin/kyc/queue/') && path.endsWith('/recheck'))
      return this.recheckKyc(path, admin.role);
    if (method === 'GET' && path.startsWith('/admin/kyc/queue/') && path.endsWith('/documents'))
      return this.kycDocumentsFor(path, admin.role);
    if (method === 'GET' && path.startsWith('/admin/kyc/queue/')) return this.getKycItem(path);
    if (method === 'POST' && path.startsWith('/admin/kyc/queue/') && path.endsWith('/review'))
      return this.reviewKyc(path, req.body, admin.id);

    // ── Deposit rails: the minimum deposit and confirmation depth per (currency, network) ──
    if (method === 'GET' && path === '/admin/assets/rails') return ok(this.assetRailsResponse());
    if (method === 'PUT' && path.startsWith('/admin/assets/rails/'))
      return this.setAssetOverride(path, req.body, admin.id, admin.role);

    // ── Fees: the spread, every basis-point fee, and the bill schedule ──
    if (method === 'GET' && path === '/admin/fees') return ok({ fees: this.fees });
    if (method === 'PUT' && path.startsWith('/admin/fees/'))
      return this.setFee(path, req.body, admin.id, admin.role);

    // ── KYC tier ceilings ──
    if (method === 'GET' && path === '/admin/limits/tiers') return this.listTierLimits(admin.role);
    if (method === 'PUT' && path.startsWith('/admin/limits/tiers/'))
      return this.setTierLimits(path, req.body, admin.id, admin.role);

    // ── transactions + reconciliation ──
    if (method === 'GET' && path === '/admin/transactions') return this.listTransactions(req.query);
    if (method === 'GET' && path.startsWith('/admin/transactions/'))
      return this.transactionDetail(path.slice('/admin/transactions/'.length));
    if (method === 'GET' && path === '/admin/reconciliation') return ok(this.reconResponse());
    if (method === 'POST' && path === '/admin/reconciliation/run') {
      this.lastRunAt = new Date().toISOString();
      return ok(this.reconResponse());
    }

    // ── brand ──
    // 404, not `ok(null)`: before the first upload the API has no asset to describe and
    // answers 404, and a mock that returned null instead hid a real bug — the console's
    // read path had no 404→null branch, so the page rendered a red error box with no
    // upload control on exactly the fresh deployment that needed one.
    if (method === 'GET' && path === '/admin/brand/logo')
      return this.brand === null
        ? fail(404, 'NOT_FOUND', 'No logo has been uploaded yet.')
        : ok(this.brand);
    if (method === 'PUT' && path === '/admin/brand/logo') return this.uploadBrand(req.body, admin.id);

    // The splash: the same 404-means-empty contract as the logo, on its own route because
    // it accepts a different set of types under a much larger ceiling.
    if (method === 'GET' && path === '/admin/brand/splash')
      return this.splash === null
        ? fail(404, 'NOT_FOUND', 'No splash screen has been uploaded yet.')
        : ok(this.splash);
    if ((method === 'PUT' || method === 'POST') && path === '/admin/brand/splash')
      return this.uploadSplash(req.body, admin.id, admin.role);

    // ── bank directory ──
    if (method === 'GET' && path === '/admin/banks') return ok(this.bankDirectory());
    if (method === 'PUT' && path.startsWith('/admin/banks/'))
      return this.uploadBankLogo(path, req.body, admin.id);

    // ── home-screen banners ──
    if (method === 'GET' && path === '/admin/banners') return ok({ banners: this.banners });
    if (method === 'POST' && path === '/admin/banners') return this.createBanner(req.body, admin.id);
    if (method === 'PUT' && path.startsWith('/admin/banners/') && path.endsWith('/image'))
      return this.uploadBannerImage(path, req.body, admin.id);
    if (method === 'PATCH' && path.startsWith('/admin/banners/'))
      return this.updateBanner(path, req.body, admin.id);
    if (method === 'DELETE' && path.startsWith('/admin/banners/')) return this.deleteBanner(path);

    // ── gift cards ──
    // Two grants on one path prefix: the catalogue is finance (`rates:write`), the review
    // queue is compliance (`kyc:review`). Mirrored here rather than gated once, because a
    // mock that let either role do both would hide the 403 the real API returns — and the
    // catalogue's *list* is gated too, not just its writes.
    if (method === 'GET' && path === '/admin/giftcards/brands') return this.listBrands(admin.role);
    if (method === 'POST' && path === '/admin/giftcards/brands')
      return this.createBrand(req.body, admin.role);
    if (method === 'PATCH' && path.startsWith('/admin/giftcards/brands/'))
      return this.updateBrand(path, req.body, admin.role);
    if (method === 'GET' && path === '/admin/giftcards/queue') return this.giftcardQueue();
    if (method === 'GET' && path.startsWith('/admin/giftcards/queue/')) return this.getRedemption(path);
    if (method === 'POST' && path.startsWith('/admin/giftcards/queue/') && path.endsWith('/review'))
      return this.reviewGiftcard(path, req.body, admin.id);

    // ── support ──
    if (method === 'GET' && path === '/admin/support/inbox') return this.supportInbox(req.query);
    if (method === 'GET' && path === '/admin/support/settings') return ok(this.supportSettings);
    if (method === 'PUT' && path === '/admin/support/settings') return this.setSupportSettings(req.body);
    if (method === 'GET' && path.startsWith('/admin/support/media/')) return this.getSupportMedia(path);
    if (method === 'POST' && path.startsWith('/admin/support/threads/') && path.endsWith('/reply'))
      return this.supportReply(path, req.body, admin.id);
    if (method === 'POST' && path.startsWith('/admin/support/threads/') && path.endsWith('/assign'))
      return this.supportAssign(path, req.body);
    if (method === 'GET' && path.startsWith('/admin/support/threads/')) return this.supportThread(path);

    // ── Business Settings (white-label config hub) ──
    // Superadmin on both verbs, as the API gates them. It matters now that the Brand assets
    // page reaches this route to *clear* the splash pointer — the only mechanism the API
    // offers for turning a splash off — so a mock that let any admin through would make that
    // control look available to a role the server refuses.
    if (path === '/admin/settings/business' && (method === 'GET' || method === 'PUT')) {
      if (admin.role !== 'superadmin') {
        return fail(403, 'FORBIDDEN', 'Only a super admin can read or change business settings.');
      }
      return method === 'GET'
        ? ok(this.platformSettings)
        : this.setBusinessSettings(req.body, admin.id);
    }

    // ── legal & about documents ──
    if (method === 'GET' && path === '/admin/legal') return ok({ documents: this.legalDocuments });
    if (method === 'PUT' && path.startsWith('/admin/legal/'))
      return this.publishLegalDocument(path, req.body, admin.id);

    // ── email templates ──
    // The suffixed paths are matched before the bare `:key` PUT, so `/preview` can never be
    // read as the name of a template.
    if (method === 'GET' && path === '/admin/email-templates')
      return ok({ templates: this.emailTemplates });
    if (method === 'POST' && path.startsWith('/admin/email-templates/') && path.endsWith('/preview'))
      return this.previewEmailTemplate(path, req.body);
    if (method === 'POST' && path.startsWith('/admin/email-templates/') && path.endsWith('/test-send'))
      return this.testSendEmailTemplate(path, req.body);
    if (method === 'POST' && path.startsWith('/admin/email-templates/') && path.endsWith('/restore'))
      return this.restoreEmailTemplate(path, admin.id);
    if (method === 'PUT' && path.startsWith('/admin/email-templates/'))
      return this.upsertEmailTemplate(path, req.body, admin.id);

    /*
     * ── the email platform: layout, image library, broadcasts, SMTP proof (superadmin) ──
     *
     * Four sibling prefixes plus one outlier, behind one role gate, matched the same way the
     * `/admin/settings/business` block above is.
     *
     * The prefixes are tested for exact equality or with their trailing slash attached
     * (`/admin/email-layout/`), never as a bare `startsWith`. That is what stops one swallowing
     * another: `/admin/email-templates` handled above and `/admin/email-images` here diverge at
     * the first character after `email-`, but a bare `startsWith('/admin/email-')` would have
     * matched both, and a bare `startsWith('/admin/email-layout')` would have matched a route
     * called `/admin/email-layouts` the day somebody adds one.
     *
     * Inside the block, every suffixed path (`/versions`, `/restore`, `/preview`, `/send`,
     * `/cancel`) is matched before the parameterised handler that would otherwise read the
     * suffix as an id.
     */
    const emailPlatform =
      path === '/admin/email-layout' ||
      path.startsWith('/admin/email-layout/') ||
      path === '/admin/email-images' ||
      path.startsWith('/admin/email-images/') ||
      path === '/admin/email-broadcasts' ||
      path.startsWith('/admin/email-broadcasts/') ||
      path === '/admin/smtp/test';
    if (emailPlatform) {
      if (admin.role !== 'superadmin') {
        return fail(403, 'FORBIDDEN', 'Only a super admin can read or change email settings.');
      }

      // The layout. `versions`, `restore` and `preview` first, then the layout itself.
      if (method === 'GET' && path === '/admin/email-layout/versions')
        return ok({ versions: this.emailLayoutVersions() });
      if (method === 'POST' && path === '/admin/email-layout/restore')
        return this.restoreEmailLayout(req.body, admin.id);
      if (method === 'POST' && path === '/admin/email-layout/preview')
        return this.previewEmailLayout(req.body);
      if (method === 'GET' && path === '/admin/email-layout') return ok(this.activeEmailLayout());
      if (method === 'PUT' && path === '/admin/email-layout')
        return this.setEmailLayout(req.body, admin.id);

      // The image library.
      if (method === 'GET' && path === '/admin/email-images') return ok({ images: this.emailImages });
      if (method === 'POST' && path === '/admin/email-images')
        return this.uploadEmailImage(req.body, admin.id);
      if (method === 'DELETE' && path.startsWith('/admin/email-images/'))
        return this.deleteEmailImage(path);

      // Broadcasts. `preview` creates nothing, so it is answered before the create on the
      // same path and the id handlers below it.
      if (method === 'POST' && path === '/admin/email-broadcasts/preview')
        return this.previewBroadcastAudience(req.body);
      if (method === 'GET' && path === '/admin/email-broadcasts')
        return ok({ broadcasts: this.emailBroadcasts.map(broadcastListShape) });
      if (method === 'POST' && path === '/admin/email-broadcasts')
        return this.createBroadcast(req.body, admin.id);
      if (method === 'POST' && path.startsWith('/admin/email-broadcasts/') && path.endsWith('/send'))
        return this.sendBroadcast(path);
      if (method === 'POST' && path.startsWith('/admin/email-broadcasts/') && path.endsWith('/cancel'))
        return this.cancelBroadcast(path);
      if (method === 'GET' && path.startsWith('/admin/email-broadcasts/'))
        return this.getBroadcast(path);

      // Proving the mail server. Nothing is saved by this route; it returns a verdict.
      if (method === 'POST' && path === '/admin/smtp/test') return this.smtpTest(req.body);
    }

    // ── push broadcast (superadmin) ──
    if (method === 'POST' && path === '/admin/push/broadcast') return this.broadcastPush(req.body, admin.role);

    // ── the treasury desk ──
    // `/reconciliation/run` is answered before `/reconciliation`, and `/review/<id>` before
    // the bare queue, because a prefix test would otherwise swallow the more specific path.
    if (method === 'GET' && path === '/admin/treasury/overview') return ok(this.treasuryOverview());
    if (method === 'GET' && path === '/admin/treasury/orders') return ok({ orders: this.treasuryOrders });
    if (method === 'GET' && path.startsWith('/admin/treasury/orders/'))
      return this.treasuryOrder(path);
    if (method === 'POST' && path.startsWith('/admin/treasury/review/'))
      return this.decideTreasuryReview(path, req.body);
    if (method === 'GET' && path === '/admin/treasury/review')
      return ok({ orders: this.treasuryOrders.filter((o) => o.reviewReason !== null) });
    if (method === 'GET' && path === '/admin/treasury/rebalances')
      return ok({ rebalances: this.treasuryRebalances });
    if (method === 'POST' && path === '/admin/treasury/rebalances')
      return this.recordTreasuryRebalance(req.body, admin.id);
    if (method === 'GET' && path === '/admin/treasury/thresholds') return ok(this.treasuryConfig);
    if (method === 'PUT' && path === '/admin/treasury/thresholds')
      return this.setTreasuryThresholds(req.body);
    if (method === 'POST' && path === '/admin/treasury/reconciliation/run')
      return this.runTreasuryReconciliation(req.body);
    if (method === 'GET' && path === '/admin/treasury/reconciliation')
      return ok({ runs: this.reconciliationRuns, openDeltas: this.reconciliationDeltas });

    return fail(404, 'NOT_FOUND', 'We could not find that.');
  }

  // ── treasury ──

  /**
   * The dashboard, derived rather than stored.
   *
   * `openReviews` and `openDeltas` are counted off the same arrays the queue and the
   * reconciliation page read, so the badge on the dashboard can never disagree with the list
   * it links to — which is the one thing a mock gets wrong most easily and the one thing an
   * operator would notice first.
   */
  private treasuryOverview(): TreasuryOverviewResponse {
    return {
      holdings: seedTreasuryHoldings(),
      ngnFloat: { currency: 'NGN', amount: '184305500' },
      ngnFloatLow: false,
      sandbox: this.treasuryConfig.sandbox,
      conversionsPaused: this.treasuryConfig.conversionsPaused,
      pausedReason: this.treasuryConfig.pausedReason,
      pausedAt: this.treasuryConfig.conversionsPaused ? new Date().toISOString() : null,
      openReviews: this.treasuryOrders.filter((o) => o.reviewReason !== null).length,
      openDeltas: this.reconciliationDeltas.length,
      lastReconciliationAt: this.reconciliationRuns[0]?.startedAt ?? null,
    };
  }

  private treasuryOrder(path: string): RawResponse {
    const id = decodeURIComponent(path.split('/').pop() ?? '');
    const found = this.treasuryOrders.find((o) => o.id === id);
    if (found === undefined) return fail(404, 'NOT_FOUND', 'No such order.');
    return ok(found);
  }

  /**
   * Decide a held order.
   *
   * The mock mirrors what the API actually does with each decision rather than just clearing
   * the flag for all four, because the point of the demo transport is that an operator
   * learning the screen learns the real consequences: `approve` releases it to the worker,
   * `reprice` restates what the customer receives, and the other two send it back.
   */
  private decideTreasuryReview(path: string, body: unknown): RawResponse {
    const id = decodeURIComponent(path.split('/').pop() ?? '');
    const order = this.treasuryOrders.find((o) => o.id === id);
    if (order === undefined) return fail(404, 'NOT_FOUND', 'No such order.');
    if (order.reviewReason === null)
      return fail(409, 'CONFLICT', 'That order is not waiting for a review decision.');

    const b = (body ?? {}) as { decision?: string };
    const decision = b.decision ?? '';
    if (!['approve', 'reprice', 'refund', 'reject'].includes(decision))
      return fail(422, 'VALIDATION_FAILED', 'That is not a decision.');

    const next: AdminTreasuryOrder = {
      ...order,
      reviewReason: null,
      status:
        decision === 'refund' || decision === 'reject'
          ? 'refund_pending'
          : order.side === 'buy'
            ? 'ngn_received'
            : 'deposit_confirmed',
      // A refund in flight still reads as `processing` to the customer — `refunding` is house
      // vocabulary and is not one of the five statuses the contract lets a customer see.
      publicStatus: 'processing',
    };
    this.treasuryOrders = this.treasuryOrders.map((o) => (o.id === id ? next : o));
    return ok(next);
  }

  private recordTreasuryRebalance(body: unknown, adminId: string): RawResponse {
    const b = (body ?? {}) as Partial<RecordRebalanceRequest>;
    if (b.from === undefined || b.to === undefined || b.kind === undefined)
      return fail(422, 'VALIDATION_FAILED', 'A rebalance needs a kind, a from and a to.');
    if (b.from.currency === b.to.currency)
      return fail(422, 'VALIDATION_FAILED', 'A rebalance moves value between two currencies.');

    const created: TreasuryRebalance = {
      id: `rb-${this.treasuryRebalances.length + 1}`,
      reference: `RBL-${String(this.treasuryRebalances.length + 1).padStart(6, '0')}`,
      kind: b.kind,
      from: b.from,
      to: b.to,
      rate: rateBetweenDTO(b.from, b.to),
      counterparty: b.counterparty ?? '',
      note: b.note ?? '',
      recordedBy: adminId,
      createdAt: new Date().toISOString(),
    };
    this.treasuryRebalances = [created, ...this.treasuryRebalances];
    return ok(created, 201);
  }

  private setTreasuryThresholds(body: unknown): RawResponse {
    const patch = (body ?? {}) as Partial<TreasuryThresholds>;
    // Currency is checked here for the same reason the API checks it: the stored figure is a
    // bare integer whose currency is implied by which threshold it is, so a value in the
    // wrong currency is silently reinterpreted rather than rejected.
    if (patch.usdtLowThreshold !== undefined && patch.usdtLowThreshold.currency !== 'USDT')
      return fail(422, 'VALIDATION_FAILED', 'usdtLowThreshold must be given in USDT.');
    if (patch.ngnFloatLowThreshold !== undefined && patch.ngnFloatLowThreshold.currency !== 'NGN')
      return fail(422, 'VALIDATION_FAILED', 'ngnFloatLowThreshold must be given in NGN.');
    if (patch.manualReviewCeiling !== undefined && patch.manualReviewCeiling.currency !== 'NGN')
      return fail(422, 'VALIDATION_FAILED', 'manualReviewCeiling must be given in NGN.');

    this.treasuryConfig = { ...this.treasuryConfig, ...patch };
    if (!this.treasuryConfig.conversionsPaused) this.treasuryConfig.pausedReason = '';
    return ok(this.treasuryConfig);
  }

  /** 202 and a queued verdict, because the real route enqueues rather than runs. */
  private runTreasuryReconciliation(body: unknown): RawResponse {
    const scope = ((body ?? {}) as { scope?: string }).scope ?? 'full';
    this.reconciliationRuns = [
      {
        id: `run-${this.reconciliationRuns.length + 1}`,
        scope: scope as ReconciliationScope,
        status: 'running',
        startedAt: new Date().toISOString(),
        finishedAt: null,
        checked: 0,
        deltas: 0,
        paused: false,
        note: 'queued from the console',
      },
      ...this.reconciliationRuns,
    ];
    return ok({ scope, status: 'queued' }, 202);
  }

  // ── helpers ──
  private adminFromToken(token: string | null): (typeof MOCK_ADMINS)[number] | null {
    if (token === null || !token.startsWith(TOKEN_PREFIX)) return null;
    const id = token.slice(TOKEN_PREFIX.length);
    return MOCK_ADMINS.find((a) => a.id === id) ?? null;
  }

  private login(body: unknown): RawResponse {
    const b = (body ?? {}) as { email?: string; password?: string; rememberMe?: unknown };
    const email = (b.email ?? '').trim().toLowerCase();
    const admin = MOCK_ADMINS.find((a) => a.email === email);
    if (admin === undefined || b.password !== DEMO_PASSWORD) {
      return fail(401, 'UNAUTHENTICATED', 'That email or password is not right.');
    }
    return ok(this.sessionFor(admin, b.rememberMe === true));
  }

  /**
   * Spend a remember-me token for a fresh hour and a fresh token.
   *
   * The reuse detection is reproduced rather than skipped, and it is the part worth having in a
   * demo: presenting a token that has already been spent burns the whole lineage, so a console
   * that fired two refreshes at once — a `StrictMode` double-effect, two tabs waking together —
   * signs the operator out of everything. That failure is invisible against an API that does
   * not implement it and immediate against one that does, so the mock implements it.
   */
  private refresh(body: unknown): RawResponse {
    const token = (body as { rememberToken?: unknown } | null)?.rememberToken;
    if (typeof token !== 'string' || token === '') {
      return fail(422, 'VALIDATION_FAILED', 'A remember-me token is required.');
    }
    const entry = this.rememberTokens[token];
    if (entry === undefined) {
      if (this.spentRememberTokens.has(token)) {
        // A replay. Everything in this lineage goes, exactly as the API does it.
        const adminId = this.spentRememberTokens.get(token);
        for (const [live, held] of Object.entries(this.rememberTokens)) {
          if (held.adminId === adminId) delete this.rememberTokens[live];
        }
        return fail(401, 'UNAUTHENTICATED', 'That session was already used. Please sign in again.');
      }
      return fail(401, 'UNAUTHENTICATED', 'Please sign in again.');
    }
    if (Date.parse(entry.expiresAt) <= Date.now()) {
      delete this.rememberTokens[token];
      return fail(401, 'UNAUTHENTICATED', 'That session has expired. Please sign in again.');
    }
    const admin = MOCK_ADMINS.find((a) => a.id === entry.adminId);
    if (admin === undefined) return fail(401, 'UNAUTHENTICATED', 'Please sign in again.');

    delete this.rememberTokens[token];
    this.spentRememberTokens.set(token, entry.adminId);
    // The lineage keeps its original end date. Rotation renews the token, never the deadline —
    // otherwise thirty days of steady use quietly becomes an indefinite session.
    //
    // And `admin` is stripped, because `adminSessionSchema` does not carry it and the API's
    // refresh route does not send it. The mock used to, which is precisely how the console
    // shipped reading `res.admin` here: against the demo the identity was there and remembered
    // sessions worked perfectly, and against the real API it was `undefined` and the operator
    // was signed out at the first rotation. A mock that answers more generously than the thing
    // it stands in for is not a lenient mock, it is a hidden bug.
    const { admin: _identity, ...session } = this.sessionFor(admin, true, entry.expiresAt);
    return ok(session);
  }

  /** One signed-in session: an hour of access, and a lineage when one was asked for. */
  private sessionFor(
    admin: (typeof MOCK_ADMINS)[number],
    remember: boolean,
    lineageEndsAt?: string,
  ): Record<string, unknown> {
    const { password: _pw, ...me } = admin;
    const accessTokenExpiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
    const base = {
      accessToken: `${TOKEN_PREFIX}${admin.id}`,
      accessTokenExpiresAt,
      admin: me,
    };
    if (!remember) return base;

    const expiresAt =
      lineageEndsAt ?? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    const rememberToken = `remember-${admin.id}-${String(this.rememberSeq++)}-abcdefghij`;
    this.rememberTokens[rememberToken] = { adminId: admin.id, expiresAt };
    return { ...base, rememberToken, rememberTokenExpiresAt: expiresAt };
  }

  private setFlag(path: string, body: unknown, adminId: string): RawResponse {
    const key = decodeURIComponent(path.slice('/admin/flags/'.length));
    const flag = this.flags.find((f) => f.key === key);
    if (flag === undefined) return fail(404, 'NOT_FOUND', 'No such flag.');
    const enabled = Boolean((body as { enabled?: boolean } | null)?.enabled);
    flag.enabled = enabled;
    flag.updatedByAdminId = adminId;
    flag.updatedAt = new Date().toISOString();
    return ok(flag);
  }

  private setProvider(path: string, body: unknown, adminId: string): RawResponse {
    const capability = decodeURIComponent(path.slice('/admin/providers/'.length));
    const cfg = this.providers.find((p) => p.capability === capability);
    if (cfg === undefined) return fail(404, 'NOT_FOUND', 'No such capability.');
    const b = (body ?? {}) as { priority?: string[]; disabled?: string[] };
    const next = {
      ...cfg,
      priority: Array.isArray(b.priority) ? b.priority : cfg.priority,
      disabled: Array.isArray(b.disabled) ? b.disabled : cfg.disabled,
      updatedByAdminId: adminId,
      updatedAt: new Date().toISOString(),
    };
    this.providers = this.providers.map((p) => (p.capability === capability ? next : p));
    return ok(next);
  }

  // ── provider credentials & activation ──

  /**
   * The console-visible evidence that a secret is stored: last four, nothing more.
   *
   * Byte-identical to the API's `lastFourOf`, because the mask is the *only* thing the
   * page can render for a secret — if the two disagreed, an operator would learn to read
   * a mask in the demo that means something else in production.
   */
  private lastFourOf(value: string): string {
    const tail = value.slice(-4);
    return tail.length === 0 ? '••••' : `••••${tail}`;
  }

  /**
   * Resolve one provider exactly as `credentials.ts::resolveOne` + `statusOf` do.
   *
   * Three rules are load-bearing and all three are copied deliberately rather than
   * approximated: a console value always beats the environment fallback (never the other
   * way round); a secret's plaintext never appears in the response whichever side it came
   * from; and `live` is `enabled && configured`, so a switch flipped on over a missing
   * required key reads as enabled-but-not-live rather than as working.
   */
  private resolveProvider(definition: MockProviderDefinition): ProviderStatus {
    const row = this.providerCreds[definition.slug];
    const fields: ProviderStatus['fields'] = [];
    const missing: string[] = [];

    for (const field of definition.fields) {
      const stored = row?.secrets[field.key]?.trim();
      const consoleValue = stored === undefined || stored === '' ? undefined : stored;
      const envRaw = field.envVar === null ? undefined : MOCK_PROVIDER_ENV[field.envVar]?.trim();
      const fallback = envRaw === undefined || envRaw === '' ? undefined : envRaw;
      const effective = consoleValue ?? fallback;
      const source: CredentialSource =
        consoleValue !== undefined ? 'console' : fallback !== undefined ? 'env' : 'unset';
      if (field.required && effective === undefined) missing.push(field.key);

      const storedMask = consoleValue === undefined ? null : this.lastFourOf(consoleValue);
      fields.push({
        key: field.key,
        label: field.label,
        hint: field.hint,
        secret: field.secret,
        required: field.required,
        envVar: field.envVar,
        // A non-secret is configuration an operator must be able to read back; a secret
        // is write-only forever.
        value: field.secret ? null : effective ?? null,
        masked: field.secret
          ? storedMask ?? (source === 'env' ? '•••• (from environment)' : null)
          : null,
        source,
      });
    }

    const configured = missing.length === 0;
    // No row means nobody has opened this provider's card. The legacy global switch
    // decides what that means, so a deployment behaves as it did before this page existed.
    const enabled = row === undefined ? MOCK_PROVIDER_DEFAULT_MODE === 'live' : row.enabled;

    return {
      slug: definition.slug,
      label: definition.label,
      description: definition.description,
      services: [...definition.services],
      enabled,
      configured,
      live: enabled && configured,
      missing,
      fields,
      // Whether this vendor refuses to serve a customer it has not verified itself. Read from
      // the catalogue definition rather than named here, so the demo cannot drift from the
      // real console's answer the way a hand-written list would.
      requiresCustomerVerification: definition.requiresCustomerVerification ?? false,
      // Static catalogue copy on the API side too, so the demo shows the same callback
      // guidance the real console does — minus the absolute URL, which the page refuses to
      // invent when there is no deployment behind it.
      webhook: definition.webhook ?? null,
      updatedByAdminId: row?.updatedByAdminId ?? null,
      updatedAt: row?.updatedAt ?? null,
    };
  }

  /**
   * The services whose simulation a customer would actually feel.
   *
   * Money out, money in, a bill that is supposed to arrive, and the identity check that gates
   * all three. A simulated `rates` feed is a wrong number on a screen; a simulated `payout` is
   * a customer told their money has been sent when it has not.
   */
  private static readonly CONSEQUENTIAL: ReadonlySet<string> = new Set([
    'payout',
    'collection',
    'biller',
    'custody',
    'kyc',
  ]);

  private providerStatusResponse(): ProviderStatusResponse {
    const providers = MOCK_PROVIDER_CATALOG.map((definition) => this.resolveProvider(definition));
    const liveSlugs = new Set(providers.filter((p) => p.live).map((p) => p.slug));
    return {
      providers,
      // Catalog order, not stored order — the live list doubles as the routing order.
      services: MOCK_PROVIDER_SERVICES.map((service) => {
        const live = MOCK_PROVIDER_CATALOG.filter(
          (d) => d.services.includes(service) && liveSlugs.has(d.slug),
        ).map((d) => d.slug);
        /*
         * `state` and `consequential` joined `ProviderServiceState` in the contract and the
         * mock had not caught up, which is a typecheck failure rather than anything a demo
         * would show — but a mock that does not compile is a console nobody can build.
         *
         * `simulated` is kept alongside `state` because the contract still carries both, and
         * the derivation is the honest one: a service with no live vendor is simulated.
         * `consequential` marks the services that move money or identity, which is the set the
         * console warns about running simulated.
         */
        const state: 'live' | 'simulated' = live.length === 0 ? 'simulated' : 'live';
        return {
          service,
          live,
          state,
          simulated: live.length === 0,
          consequential: MockTransport.CONSEQUENTIAL.has(service),
        };
      }),
      defaultMode: MOCK_PROVIDER_DEFAULT_MODE,
      environment: MOCK_PROVIDER_ENVIRONMENT,
    };
  }

  /**
   * The sparse patch, mirrored exactly.
   *
   * A field absent from `values` keeps its stored value; an explicit empty string clears
   * it. Anything looser and the demo would show a save quietly wiping every secret the
   * operator did not retype — the exact failure the API's sparse patch exists to prevent,
   * and one the console cannot avoid on its own because it can never render a stored
   * secret back into an input.
   */
  private setProviderCredentials(
    path: string,
    body: unknown,
    adminId: string,
    role: string,
  ): RawResponse {
    // The API gates this on `providers:write` (finance, superadmin), not on plain admin.
    if (role !== 'finance' && role !== 'superadmin') {
      return fail(403, 'FORBIDDEN', 'Your role cannot change provider credentials.');
    }
    const slug = decodeURIComponent(path.slice('/admin/provider-credentials/'.length));
    const definition = MOCK_PROVIDER_CATALOG.find((d) => d.slug === slug);
    if (definition === undefined) return fail(404, 'NOT_FOUND', 'We could not find that.');

    const b = (body ?? {}) as { enabled?: boolean; values?: Record<string, string> };
    const patch = b.values ?? {};
    const known = new Set(definition.fields.map((f) => f.key));
    for (const key of Object.keys(patch)) {
      if (!known.has(key)) {
        return fail(422, 'VALIDATION_FAILED', `${definition.label} has no field named "${key}"`);
      }
    }

    const current = this.providerCreds[slug];
    const secrets: Record<string, string> = { ...(current?.secrets ?? {}) };
    for (const [key, raw] of Object.entries(patch)) {
      const value = typeof raw === 'string' ? raw.trim() : '';
      if (value === '') delete secrets[key];
      else secrets[key] = value;
    }

    this.providerCreds[slug] = {
      enabled: b.enabled ?? current?.enabled ?? MOCK_PROVIDER_DEFAULT_MODE === 'live',
      secrets,
      updatedByAdminId: adminId,
      updatedAt: new Date().toISOString(),
    };
    return ok(this.resolveProvider(definition));
  }

  // ── live rates + operator override ──

  /**
   * Re-derive `stale` and `effective` from the clock on every read, rather than trusting
   * what the seed or the last write left behind. It costs nothing and it means the demo's
   * ETH row genuinely ages past the ceiling while a tab is left open, which is the one
   * behaviour a screenshot of this page cannot show.
   */
  private rateBoard(): { rows: AdminRateRow[]; maxAgeSeconds: number; generatedAt: string } {
    const now = Date.now();
    this.rates = this.rates.map((row) => {
      const stale =
        row.live === null ||
        Date.parse(row.live.observedAt) <= now - MOCK_RATE_MAX_AGE_SECONDS * 1000;
      // A null `expiresAt` is an override that does not lapse on its own — which is every
      // override written today. Only a historical row from when they were time-boxed
      // carries a date, and only that kind can age out from under the operator.
      const override =
        row.override !== null &&
        (row.override.expiresAt === null || Date.parse(row.override.expiresAt) > now)
          ? row.override
          : null;
      return {
        ...row,
        live: row.live === null ? null : { ...row.live, stale },
        override,
        // Freshness first, price second — the same order the API applies, so a stale feed
        // prices nothing whether or not an override stands.
        effective:
          row.live === null || stale
            ? null
            : override !== null
              ? { numerator: override.numerator, denominator: override.denominator }
              : { numerator: row.live.numerator, denominator: row.live.denominator },
      };
    });
    return {
      rows: this.rates,
      maxAgeSeconds: MOCK_RATE_MAX_AGE_SECONDS,
      generatedAt: new Date(now).toISOString(),
    };
  }

  private ratePairFrom(path: string): { base: string; quote: string } | null {
    const segments = path.slice('/admin/rates/'.length).split('/');
    if (segments.length !== 2) return null;
    const [base, quote] = segments;
    if (base === undefined || quote === undefined || base === '' || quote === '') return null;
    return { base: decodeURIComponent(base), quote: decodeURIComponent(quote) };
  }

  private setRateOverride(path: string, body: unknown, adminId: string, role: string): RawResponse {
    if (role !== 'finance' && role !== 'superadmin') {
      return fail(403, 'FORBIDDEN', 'Your role cannot change rates.');
    }
    const pair = this.ratePairFrom(path);
    if (pair === null) return fail(404, 'NOT_FOUND', 'No such pair.');
    const index = this.rates.findIndex((r) => r.base === pair.base && r.quote === pair.quote);
    if (index === -1) return fail(404, 'NOT_FOUND', 'No such pair.');

    const price = ((body ?? {}) as { price?: string }).price ?? '';
    const rational = toRational(price);
    if (rational === null) {
      return fail(422, 'VALIDATION_FAILED', 'That is not a plain decimal price above zero.');
    }

    const now = Date.now();
    this.rates[index] = {
      ...this.rates[index]!,
      override: {
        ...rational,
        setByAdminId: adminId,
        setAt: new Date(now).toISOString(),
        // Six hours, mirroring the API's `RATE_OVERRIDE_TTL_SECONDS`.
        expiresAt: new Date(now + 6 * 60 * 60 * 1000).toISOString(),
      },
    };
    this.rateBoard();
    return ok(this.rates[index]);
  }

  private clearRateOverride(path: string, role: string): RawResponse {
    if (role !== 'finance' && role !== 'superadmin') {
      return fail(403, 'FORBIDDEN', 'Your role cannot change rates.');
    }
    const pair = this.ratePairFrom(path);
    if (pair === null) return fail(404, 'NOT_FOUND', 'No such pair.');
    const index = this.rates.findIndex((r) => r.base === pair.base && r.quote === pair.quote);
    if (index === -1) return fail(404, 'NOT_FOUND', 'No such pair.');

    this.rates[index] = { ...this.rates[index]!, override: null };
    this.rateBoard();
    return ok(this.rates[index]);
  }

  private resetAllRates(role: string): RawResponse {
    if (role !== 'finance' && role !== 'superadmin') {
      return fail(403, 'FORBIDDEN', 'Your role cannot change rates.');
    }
    this.rates = this.rates.map((row) => ({ ...row, override: null }));
    return ok(this.rateBoard());
  }

  /**
   * The customer list.
   *
   * Search now runs over the full number as well as the mask, which is the whole reason an
   * operator wants the full number on this page: they are handed a number by a customer on a
   * call and need the account behind it, and a mask cannot be searched with the digits they
   * were just read.
   *
   * The projection drops every detail-only field, the new ones included. The row and the
   * detail are different shapes in the contract and a mock that returned the detail from the
   * list route would let a page ship that read a field the real list does not carry.
   */
  private listUsers(q?: string): RawResponse {
    const query = (q ?? '').trim().toLowerCase();
    const digits = query.replace(/[^0-9]/g, '');
    const items = this.users
      .filter(
        (u) =>
          query === '' ||
          (u.displayName ?? '').toLowerCase().includes(query) ||
          (u.email ?? '').toLowerCase().includes(query) ||
          (u.username ?? '').toLowerCase().includes(query) ||
          u.maskedPhone.toLowerCase().includes(query) ||
          (digits !== '' && (u.phone ?? '').includes(digits)) ||
          u.tier.includes(query) ||
          u.status.includes(query),
      )
      .map(
        ({
          limits: _l,
          usage: _u,
          walletBalance: _w,
          kycStatus: _k,
          lastActiveAt: _la,
          suspendedReason: _sr,
          sessions: _s,
          inactiveAt: _ia,
          lockedUntil: _lu,
          pinResetRequired: _pr,
          balances: _b,
          kycDocuments: _kd,
          avatar: _av,
          ...row
        }) => ({
          ...row,
          /*
           * The photo stays on the row. Every other detail-only field is dropped, but the
           * avatar is what the Users table draws, and a list that stripped it would leave the
           * table rendering lettermarks for customers who plainly have a photo on their record.
           */
          ...(_av === undefined ? {} : { avatar: _av }),
        }),
      );
    return ok({ items, nextCursor: null });
  }

  private getUser(path: string): RawResponse {
    const id = decodeURIComponent(path.slice('/admin/users/'.length));
    const user = this.users.find((u) => u.id === id);
    return user === undefined ? fail(404, 'NOT_FOUND', 'No such user.') : ok(user);
  }

  private suspendUser(path: string, body: unknown): RawResponse {
    const id = decodeURIComponent(path.slice('/admin/users/'.length, path.length - '/suspend'.length));
    const idx = this.users.findIndex((u) => u.id === id);
    if (idx === -1) return fail(404, 'NOT_FOUND', 'No such user.');
    const reason = ((body as { reason?: string } | null)?.reason ?? 'Suspended by operator.').slice(0, 200);
    const updated = { ...this.users[idx]!, status: 'suspended' as const, suspendedReason: reason };
    this.users[idx] = updated;
    return ok(updated);
  }

  private unsuspendUser(path: string): RawResponse {
    const id = decodeURIComponent(path.slice('/admin/users/'.length, path.length - '/unsuspend'.length));
    const idx = this.users.findIndex((u) => u.id === id);
    if (idx === -1) return fail(404, 'NOT_FOUND', 'No such user.');
    const updated = { ...this.users[idx]!, status: 'active' as const, suspendedReason: null };
    this.users[idx] = updated;
    return ok(updated);
  }

  // -- account state --

  /** Find the customer, check the grant, apply a patch, hand back the whole record. */
  private mutateUser(
    path: string,
    suffix: string,
    permission: 'users:write' | 'rates:write',
    role: string,
    patch: (user: AdminUserDetail) => AdminUserDetail | RawResponse,
  ): RawResponse {
    if (!adminCan(role as Parameters<typeof adminCan>[0], permission)) {
      return fail(
        403,
        'FORBIDDEN',
        permission === 'rates:write'
          ? 'Your role cannot move a customer balance.'
          : 'Your role cannot change account states.',
      );
    }
    const id = decodeURIComponent(path.slice('/admin/users/'.length, path.length - suffix.length));
    const idx = this.users.findIndex((u) => u.id === id);
    if (idx === -1) return fail(404, 'NOT_FOUND', 'No such user.');
    const next = patch(this.users[idx]!);
    // The patch may bail out with a typed failure instead of a record — a currency the customer
    // does not hold, an overdraw. `RawResponse` is the only thing here carrying `ok`, so that
    // is what separates the two.
    if (isRawResponse(next)) return next;
    this.users[idx] = next;
    return ok(next);
  }

  /**
   * Set the account state.
   *
   * Reproduces the contract's refine rather than approximating it: a reason is required on
   * everything except returning to `active`, and `closed` is refused outright because closing
   * is the customer's own act. A demo that accepted either would let a console ship whose
   * refusal path had never been seen.
   */
  private setAccountState(path: string, body: unknown, role: string): RawResponse {
    const b = (body ?? {}) as { state?: unknown; reason?: unknown; lockMinutes?: unknown };
    const settable: readonly string[] = ['active', 'suspended', 'inactive', 'locked'];
    if (typeof b.state !== 'string' || !settable.includes(b.state)) {
      return fail(
        422,
        'VALIDATION_FAILED',
        b.state === 'closed'
          ? 'Only the customer can close an account.'
          : 'That is not an account state an operator can set.',
      );
    }
    const state = b.state as OperatorSettableState;
    const reason = typeof b.reason === 'string' ? b.reason.trim().slice(0, 500) : '';
    if (state !== 'active' && reason === '') {
      return fail(422, 'VALIDATION_FAILED', 'A reason is required when restricting an account.');
    }
    const lockMinutes =
      typeof b.lockMinutes === 'number' && Number.isInteger(b.lockMinutes) ? b.lockMinutes : 60;
    if (state === 'locked' && (lockMinutes < 1 || lockMinutes > 43_200)) {
      return fail(422, 'VALIDATION_FAILED', 'A lockout runs from one minute to thirty days.');
    }

    return this.mutateUser(path, '/state', 'users:write', role, (user) => ({
      ...user,
      status: state,
      // Each field is cleared when its own state is not the one in force, so the record cannot
      // end up saying "suspended" while still carrying last week's lockout expiry beside it.
      suspendedReason: state === 'suspended' ? reason : null,
      lockedUntil:
        state === 'locked' ? new Date(Date.now() + lockMinutes * 60_000).toISOString() : null,
      inactiveAt: state === 'inactive' ? new Date().toISOString() : null,
      // Returning to active clears the PIN lockout too, which is what an operator clearing a
      // lockout for a customer on the phone actually means by it.
      pinResetRequired: state === 'active' ? false : user.pinResetRequired,
    }));
  }

  /**
   * Credit or debit a balance.
   *
   * The arithmetic is `BigInt` over the stored integer-minor-unit string, for the same reason
   * the console parses with `BigInt`: this is the demo backend, so it stands in for a ledger,
   * and a mock that rounded through a float would teach a page to trust a number the real
   * ledger would never produce.
   *
   * A debit past the balance is refused rather than allowed to go negative. The real ledger is
   * the authority on whether an overdraw is permitted, and a mock that guessed "allowed" would
   * let a page ship whose refusal path had never once been seen.
   */
  private adjustBalance(path: string, body: unknown, role: string): RawResponse {
    const b = (body ?? {}) as {
      direction?: unknown;
      amount?: unknown;
      narration?: unknown;
      idempotencyKey?: unknown;
    };
    if (b.direction !== 'credit' && b.direction !== 'debit') {
      return fail(422, 'VALIDATION_FAILED', 'Say whether this is a credit or a debit.');
    }
    const money = b.amount as { currency?: unknown; amount?: unknown } | null;
    if (
      money === null ||
      typeof money !== 'object' ||
      typeof money.currency !== 'string' ||
      typeof money.amount !== 'string' ||
      !/^[0-9]+$/.test(money.amount) ||
      /^0+$/.test(money.amount)
    ) {
      return fail(422, 'VALIDATION_FAILED', 'An amount above zero is required.');
    }
    if (typeof b.narration !== 'string' || b.narration.trim().length < 3) {
      return fail(422, 'VALIDATION_FAILED', 'A narration of at least three characters is required.');
    }
    if (typeof b.idempotencyKey !== 'string' || !/^[A-Za-z0-9_-]{16,128}$/.test(b.idempotencyKey)) {
      return fail(422, 'VALIDATION_FAILED', 'An idempotency key is required.');
    }

    const direction = b.direction;
    const amount = BigInt(money.amount);
    const currency = money.currency;
    const narration = b.narration.trim();
    const key = b.idempotencyKey;

    /*
     * Idempotency, reproduced rather than skipped.
     *
     * The one duplicate an operator cannot see afterwards without reconciling by hand is a
     * double-tapped credit, which is exactly why the contract made the key required. A mock
     * that ignored it would let a console ship that minted a fresh key per click and looked
     * fine right up until it moved somebody's money twice.
     */
    const seen = this.balanceAdjustments[key];
    if (seen !== undefined) return ok(seen);

    let receipt: AdminAdjustBalanceResponse | null = null;
    const result = this.mutateUser(path, '/balance', 'rates:write', role, (user) => {
      const wallet = user.balances.find((balance) => balance.currency === currency) ?? null;
      if (wallet === null) {
        return fail(422, 'VALIDATION_FAILED', `This customer holds no ${currency} balance.`);
      }
      const current = BigInt(wallet.amount);
      const next = direction === 'credit' ? current + amount : current - amount;
      if (next < 0n) {
        return fail(422, 'VALIDATION_FAILED', 'That would take the balance below zero.');
      }
      const after = { ...wallet, amount: next.toString() };
      receipt = {
        entryId: `le000000-0000-4000-8000-${String(700000 + this.adjustmentSeq++).padStart(12, '0')}`,
        reference: `GX-ADJ-${this.adjustmentSeq.toString(36).toUpperCase().padStart(4, '0')}`,
        direction,
        amount: { currency: after.currency, amount: amount.toString() },
        balance: after,
        currency: after.currency,
        narration,
        createdAt: new Date().toISOString(),
      };
      return {
        ...user,
        balances: user.balances.map((balance) =>
          balance.currency === currency ? after : balance,
        ),
        // The naira headline follows the naira wallet, and only the naira wallet.
        walletBalance:
          currency === user.walletBalance.currency ? after : user.walletBalance,
      };
    });
    if (!result.ok || receipt === null) return result;

    /*
     * What is deliberately NOT here: anything customer-facing.
     *
     * The activity row this write produces has to read "Top Up" with the platform icon and
     * carry no admin attribution. That is the API's job, in its serializer, inside the same
     * transaction as the posting. A mock that faked it would make the requirement look
     * satisfied when nothing implements it.
     */
    this.balanceAdjustments[key] = receipt;
    return ok(receipt);
  }

  /** The documents on one submission. Metadata only, gated exactly as the queue read is. */
  private kycDocumentsFor(path: string, role: string): RawResponse {
    if (!adminCan(role as Parameters<typeof adminCan>[0], 'kyc:read')) {
      return fail(403, 'FORBIDDEN', 'Your role cannot view identity documents.');
    }
    const submissionId = decodeURIComponent(
      path.slice('/admin/kyc/queue/'.length, path.length - '/documents'.length),
    );
    const documents = this.users
      .flatMap((user) => user.kycDocuments)
      .filter((document) => document.submissionId === submissionId);
    return ok({ documents });
  }

  // -- bill services --

  private billServiceIdFrom(path: string, suffix: string): string {
    return decodeURIComponent(
      path.slice('/admin/bills/services/'.length, path.length - suffix.length),
    );
  }

  private patchBillService(path: string, body: unknown, role: string): RawResponse {
    if (!adminCan(role as Parameters<typeof adminCan>[0], 'rates:write')) {
      return fail(403, 'FORBIDDEN', 'Your role cannot change the bill catalogue.');
    }
    const id = this.billServiceIdFrom(path, '');
    const idx = this.billServices.findIndex((service) => service.id === id);
    if (idx === -1) return fail(404, 'NOT_FOUND', 'No such bill service.');

    const b = (body ?? {}) as { mark?: unknown; color?: unknown; available?: unknown };
    const next = { ...this.billServices[idx]! };

    if (b.mark !== undefined) {
      if (typeof b.mark !== 'string' || b.mark.trim() === '' || [...b.mark.trim()].length > 6) {
        return fail(422, 'VALIDATION_FAILED', 'A mark is one to six characters.');
      }
      next.mark = b.mark.trim();
    }
    if (b.color !== undefined) {
      if (typeof b.color !== 'string' || !/^#[0-9A-Fa-f]{6}$/.test(b.color.trim())) {
        return fail(422, 'VALIDATION_FAILED', 'A colour is six hex digits, like #1E88E5.');
      }
      next.color = b.color.trim().toUpperCase();
    }
    if (b.available !== undefined) {
      if (typeof b.available !== 'boolean') {
        return fail(422, 'VALIDATION_FAILED', 'Availability is true or false.');
      }
      next.available = b.available;
    }

    this.billServices[idx] = next;
    return ok(next);
  }

  /**
   * Store a service icon.
   *
   * The declared mime is checked against the *bytes* rather than trusted, because that is what
   * the API does and a demo that accepted a renamed SVG would let the console ship with its
   * refusal path never once exercised. A content type is a claim; a magic number is evidence.
   */
  private uploadBillIcon(path: string, body: unknown, role: string): RawResponse {
    if (!adminCan(role as Parameters<typeof adminCan>[0], 'rates:write')) {
      return fail(403, 'FORBIDDEN', 'Your role cannot change the bill catalogue.');
    }
    const id = this.billServiceIdFrom(path, '/icon');
    const idx = this.billServices.findIndex((service) => service.id === id);
    if (idx === -1) return fail(404, 'NOT_FOUND', 'No such bill service.');

    const b = (body ?? {}) as { mime?: unknown; data?: unknown };
    if (typeof b.mime !== 'string' || typeof b.data !== 'string' || b.data === '') {
      return fail(422, 'VALIDATION_FAILED', 'An icon image is required.');
    }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(b.mime)) {
      return fail(422, 'VALIDATION_FAILED', 'PNG, JPG or WebP only.');
    }
    const bytes = decodeBase64(b.data);
    if (bytes === null) return fail(422, 'VALIDATION_FAILED', 'That image could not be read.');
    // The contract's `BILL_ICON_MAX_BYTES`. Far below a brand logo's ceiling, deliberately: a
    // service chip is drawn at 96 px and anything larger is a mistake rather than a choice.
    if (bytes.byteLength > 200_000) {
      return fail(422, 'VALIDATION_FAILED', 'Keep the icon under 200 KB.');
    }
    const sniffed = sniffIconMime(bytes);
    if (sniffed !== b.mime) {
      return fail(
        422,
        'VALIDATION_FAILED',
        'Those bytes are not the image type the upload claims they are. An icon is rendered in the app, so the file itself has to be a raster image.',
      );
    }

    BILL_ICONS[id] = { mime: b.mime, name: `${id}.png`, dataBase64: b.data };
    const next: BillService = {
      ...this.billServices[idx]!,
      iconPath: `/v1/bills/services/${id}/icon?v=${String(Date.now())}`,
    };
    this.billServices[idx] = next;
    return ok(next);
  }

  private getKycItem(path: string): RawResponse {
    const id = decodeURIComponent(path.slice('/admin/kyc/queue/'.length));
    const item = this.kyc.find((k) => k.id === id);
    if (item === undefined) return fail(404, 'NOT_FOUND', 'That review is no longer pending.');
    return ok({ ...item, status: 'pending', reason: null, reviewedAt: null, reviewedByAdminId: null });
  }

  private reviewKyc(path: string, body: unknown, adminId: string): RawResponse {
    const id = decodeURIComponent(
      path.slice('/admin/kyc/queue/'.length, path.length - '/review'.length),
    );
    const idx = this.kyc.findIndex((k) => k.id === id);
    if (idx === -1) return fail(404, 'NOT_FOUND', 'That review is no longer pending.');
    const decision = (body as { decision?: string } | null)?.decision;
    if (decision !== 'approve' && decision !== 'reject') {
      return fail(422, 'VALIDATION_FAILED', 'A decision is required.');
    }
    const reason = (body as { reason?: string } | null)?.reason ?? null;
    if (decision === 'reject' && (reason === null || reason.trim() === '')) {
      return fail(422, 'VALIDATION_FAILED', 'A rejection needs a reason.');
    }
    const item = this.kyc[idx]!;
    this.kyc.splice(idx, 1);
    return ok({
      ...item,
      status: decision === 'approve' ? 'verified' : 'rejected',
      reason: decision === 'reject' ? reason : null,
      reviewedAt: new Date().toISOString(),
      reviewedByAdminId: adminId,
    });
  }

  /**
   * Filter, then page.
   *
   * `limit` and `cursor` are honoured rather than ignored because the console now builds
   * a dashboard window by walking `nextCursor`, and a mock that returned everything in
   * one page would let a paging bug ship undetected to the only deployment that pages.
   * `limit` is clamped to the contract's 1–100 with the same default of 50; the cursor is
   * the last row id of the previous page.
   */
  private listTransactions(query?: RawRequest['query']): RawResponse {
    const kind = query?.['kind'];
    const status = query?.['status'];
    const matching = this.transactions.filter(
      (t) => (kind === undefined || t.kind === kind) && (status === undefined || t.status === status),
    );

    const requested = Number(query?.['limit'] ?? 50);
    const limit = Number.isInteger(requested) ? Math.min(Math.max(requested, 1), 100) : 50;
    const cursor = query?.['cursor'];
    const start = cursor === undefined ? 0 : matching.findIndex((t) => t.id === cursor) + 1;
    const items = matching.slice(start, start + limit);
    const nextCursor =
      start + limit < matching.length ? items[items.length - 1]?.id ?? null : null;
    return ok({ items, nextCursor });
  }

  /**
   * One movement in full, for the panel the demo console opens from a row.
   *
   * Built from the seeded list row rather than from a second fixture, so the demo cannot show
   * an amount in the panel that disagrees with the amount in the row behind it. The facts are
   * the ones every kind has; the kind-specific ones are the real API's job and a mock that
   * invented them would be teaching the layout against data that does not exist.
   */
  private transactionDetail(id: string): RawResponse {
    const row = this.transactions.find((t) => t.id === decodeURIComponent(id));
    if (row === undefined) return fail(404, 'NOT_FOUND', 'That transaction does not exist.');

    const user = this.users.find((u) => u.id === row.userId);
    return ok({
      row,
      customer:
        user === undefined
          ? null
          : {
              id: user.id,
              name: user.displayName ?? '',
              email: user.email,
              phone: user.phone,
              maskedPhone: row.maskedPhone,
              tier: user.tier,
              avatar: user.avatar ?? null,
            },
      // Deliberately thin. The kind-specific facts are assembled by the real API from the
      // source table, and a mock that invented plausible ones would teach this layout against
      // data that does not exist - which is how a demo ends up being the thing that was
      // designed against. The amount is already in the panel's own heading, so repeating it
      // here as a raw minor-unit figure would only look like a bug.
      facts: [{ label: 'Exact wording of the status', value: row.status }],
      postings: [],
      charges:
        row.providerCost === null
          ? []
          : [
              {
                what: 'Payment gateway charge',
                vendor: row.providerCost.vendor,
                amount: row.providerCost.amount,
                note: '',
                at: row.createdAt,
              },
            ],
      timeline: [{ label: 'Started', at: row.createdAt }],
    });
  }

  private reconResponse(): ReconciliationResponse {
    const rows = this.recon;
    const drifted = rows.filter((r) => !/^-?0+$/.test(r.drift.amount)).length;
    return {
      rows,
      totals: { accounts: rows.length, drifted, inBalance: drifted === 0 },
      runAt: this.lastRunAt,
    };
  }

  private uploadBrand(body: unknown, adminId: string): RawResponse {
    const b = (body ?? {}) as { mime?: string; dataBase64?: string };
    if (typeof b.mime !== 'string' || typeof b.dataBase64 !== 'string' || b.dataBase64.length === 0) {
      return fail(422, 'VALIDATION_FAILED', 'A logo image is required.');
    }
    this.brand = {
      key: 'brand/logo',
      mime: b.mime as BrandAsset['mime'],
      updatedAt: new Date().toISOString(),
      updatedByAdminId: adminId,
    };
    return ok(this.brand);
  }

  /**
   * The splash upload, with as much of the server's validation as a browser mock can honestly
   * perform — and an explicit note about the one rule it cannot.
   *
   * Mirrored exactly: superadmin only; the mime allowlist (`SPLASH_ASSET_MIMES`, which is the
   * raster set **plus** `video/mp4` and no SVG); the 6 MB decoded ceiling; the magic-number
   * sniff that refuses a payload lying about its type; and the portrait refusal for a PNG.
   *
   * Not mirrored: the portrait check for JPEG and WebP. The API reads those dimensions by
   * walking a JPEG segment chain and decoding a WebP variant header, and reimplementing both
   * here would be eighty lines of header parsing in a demo transport. A PNG's size is a
   * fixed-offset read, so that case is done properly and the headline rule is exercised. The
   * console's own pre-check measures every type with a real `Image`, so an operator still
   * meets the refusal before the round-trip — it is only this backend that is less strict
   * than the real one, and only in the safe direction of accepting something production
   * would refuse rather than refusing something it would take.
   */
  private uploadSplash(body: unknown, adminId: string, role: string): RawResponse {
    if (role !== 'superadmin') {
      return fail(403, 'FORBIDDEN', 'Only a super admin can change brand assets.');
    }
    const b = (body ?? {}) as { mime?: string; dataBase64?: string };
    if (typeof b.dataBase64 !== 'string' || b.dataBase64.length === 0) {
      return fail(422, 'VALIDATION_FAILED', 'dataBase64: a splash file is required.');
    }
    if (typeof b.mime !== 'string' || !SPLASH_MIMES.includes(b.mime)) {
      return fail(422, 'VALIDATION_FAILED', 'mime: a splash must be a PNG, JPEG, WebP or MP4.');
    }

    const bytes = decodeBase64(b.dataBase64);
    if (bytes === null) return fail(422, 'VALIDATION_FAILED', 'dataBase64: splash is not valid base64');
    if (bytes.length > SPLASH_MAX_BYTES) {
      return fail(
        422,
        'VALIDATION_FAILED',
        `dataBase64: splash is ${bytes.length} bytes; the limit is ${SPLASH_MAX_BYTES}` +
          ' (the blob travels in one MySQL packet and base64 inflates it by a third)',
      );
    }

    const sniffed = sniffSplashMime(bytes);
    if (sniffed === null) {
      return fail(
        422,
        'VALIDATION_FAILED',
        'dataBase64: unrecognised file type. A splash must be a PNG, JPEG, WebP or MP4',
      );
    }
    if (sniffed !== b.mime) {
      return fail(422, 'VALIDATION_FAILED', `mime: declared ${b.mime} but the bytes are ${sniffed}`);
    }

    // A video's aspect lives in a `tkhd` box inside `moov`, which the API does not walk
    // either — so the portrait rule is an image rule on both sides, and null here is the
    // same honest "not measured" the API returns.
    const dimensions = sniffed === 'image/png' ? pngDimensions(bytes) : null;
    if (dimensions !== null && dimensions.width > dimensions.height) {
      return fail(
        422,
        'VALIDATION_FAILED',
        `dataBase64: the splash must be portrait, but this image is ${dimensions.width}×${dimensions.height}` +
          ' (landscape). It is rendered full-bleed on a phone at launch',
      );
    }

    this.splash = {
      key: 'splash',
      mime: sniffed,
      bytes: bytes.length,
      width: dimensions?.width ?? null,
      height: dimensions?.height ?? null,
      updatedAt: new Date().toISOString(),
      updatedByAdminId: adminId,
    };
    // The upload points the settings singleton at the asset in the same transaction the API
    // does, so a demo that uploads and then reads Business Settings sees the pointer set —
    // and, more to the point, so *clearing* it from the console has something to clear.
    this.platformSettings = { ...this.platformSettings, splashAssetKey: 'splash' };
    return ok(this.splash);
  }

  // ── KYC tier ceilings ──
  //
  // Gated on the KYC-review grant — compliance and superadmin — on the *read* as well as the
  // write, because the ceilings are compliance policy rather than something every operator
  // needs. Support can already see one customer's limits on the user detail, which is what
  // answering a ticket takes.

  // ── Deposit rails ───────────────────────────────────────────────────────────────

  /**
   * The board, always reporting `custodianMinimumsRead: false`.
   *
   * The demo has no custodian to ask, and claiming otherwise would show an empty custodian
   * column that an operator reads as "NOWPayments has no floor for this rail" — the opposite
   * conclusion from "we could not ask it".
   */
  private assetRailsResponse(): { rails: AdminAssetRail[]; custodianMinimumsRead: boolean } {
    return { rails: this.assetRails, custodianMinimumsRead: false };
  }

  /**
   * Override or clear one rail, mirroring the API's three-state patch: a field left out
   * keeps what is stored, an explicit `null` clears the override back to the catalogue, and
   * a value overrides. Collapsing "clear" into "absent" here would make the demo teach the
   * wrong mental model of a page whose whole point is that an un-overridden rail keeps
   * tracking the catalogue.
   */
  private setAssetOverride(path: string, body: unknown, adminId: string, role: string): RawResponse {
    if (role !== 'finance' && role !== 'superadmin') {
      return fail(403, 'FORBIDDEN', 'Your role cannot change deposit minimums.');
    }
    const segments = path.slice('/admin/assets/rails/'.length).split('/');
    const currency = decodeURIComponent(segments[0] ?? '');
    const network = decodeURIComponent(segments[1] ?? '');
    const index = this.assetRails.findIndex(
      (row) => row.rail.currency === currency && row.rail.network === network,
    );
    if (index === -1) return fail(404, 'NOT_FOUND', 'No such deposit rail.');

    const current = this.assetRails[index]!;
    const b = (body ?? {}) as { minDeposit?: string | null; confirmationsRequired?: number | null };

    if (b.minDeposit !== undefined && b.minDeposit !== null && !/^\d{1,20}(\.\d{1,18})?$/.test(b.minDeposit.trim())) {
      return fail(422, 'VALIDATION_FAILED', 'minDeposit must be a decimal amount, e.g. 0.0005');
    }
    if (
      b.confirmationsRequired !== undefined &&
      b.confirmationsRequired !== null &&
      (!Number.isInteger(b.confirmationsRequired) ||
        b.confirmationsRequired < 0 ||
        b.confirmationsRequired > 1000)
    ) {
      return fail(422, 'VALIDATION_FAILED', 'confirmationsRequired must be a whole number, 0–1000');
    }

    const overrideMinDeposit =
      b.minDeposit === undefined ? current.overrideMinDeposit : b.minDeposit === null ? null : b.minDeposit.trim();
    const overrideConfirmations =
      b.confirmationsRequired === undefined ? current.overrideConfirmations : b.confirmationsRequired;
    const cleared = overrideMinDeposit === null && overrideConfirmations === null;

    const updated: AdminAssetRail = {
      rail: current.rail,
      custodianMinDeposit: null,
      overrideMinDeposit,
      overrideConfirmations,
      effectiveMinDeposit: overrideMinDeposit ?? current.rail.minDeposit,
      minDepositSource: overrideMinDeposit === null ? 'catalogue' : 'operator',
      effectiveConfirmations: overrideConfirmations ?? current.rail.confirmationsRequired,
      confirmationsSource: overrideConfirmations === null ? 'catalogue' : 'operator',
      updatedByAdminId: cleared ? null : adminId,
      updatedAt: cleared ? null : new Date().toISOString(),
    };
    this.assetRails[index] = updated;
    return ok(updated);
  }

  /**
   * Set or clear one fee knob, mirroring the API's two states exactly: a value overrides,
   * and an explicit `null` clears the override so the knob goes back to its environment
   * variable — or, for the instant-settlement pair, to tracking the manual conversion knob.
   *
   * The re-resolve afterwards is the part worth having in a mock. Change the conversion fee
   * while instant settlement is inheriting and instant settlement moves with it, in the same
   * response; override instant settlement first and it stops moving. That is the whole
   * mental model this page has to teach, and a demo that stored the numbers independently
   * would teach the drift bug instead.
   */
  private setFee(path: string, body: unknown, adminId: string, role: string): RawResponse {
    if (role !== 'finance' && role !== 'superadmin') {
      return fail(403, 'FORBIDDEN', 'Your role cannot change fees.');
    }
    const key = decodeURIComponent(path.slice('/admin/fees/'.length));
    const index = this.fees.findIndex((row) => row.key === key);
    if (index === -1) return fail(404, 'NOT_FOUND', 'No such fee.');

    const current = this.fees[index]!;
    const b = (body ?? {}) as { value?: string | null };
    if (b.value === undefined) {
      return fail(422, 'VALIDATION_FAILED', 'value is required; send null to clear the override');
    }
    if (b.value !== null) {
      if (!/^\d{1,15}$/.test(b.value.trim())) {
        return fail(422, 'VALIDATION_FAILED', 'value must be a whole number, e.g. 25');
      }
      if (BigInt(b.value.trim()) > BigInt(current.maxValue)) {
        return fail(422, 'VALIDATION_FAILED', `${current.label} cannot exceed ${current.maxValue}`);
      }
    }

    const overrideValue = b.value === null ? null : b.value.trim();
    this.fees[index] = {
      ...current,
      overrideValue,
      updatedByAdminId: overrideValue === null ? null : adminId,
      updatedAt: overrideValue === null ? null : new Date().toISOString(),
    };
    this.resolveFees();
    return ok(this.fees[index]);
  }

  /**
   * Re-apply the precedence across the whole board: override beats fallback, and a knob with
   * no override reads through to its environment value or, one level up, to the knob it
   * tracks. Run after every write because a change to `conversion_fee_bps` is also a change
   * to whatever is inheriting it.
   */
  private resolveFees(): void {
    const settled = new Map<string, string>();
    for (const row of this.fees) {
      if (row.inheritsFrom !== null) continue;
      settled.set(row.key, row.overrideValue ?? row.envValue ?? '0');
    }
    this.fees = this.fees.map((row) => {
      if (row.overrideValue !== null) {
        return { ...row, effectiveValue: row.overrideValue, source: 'operator' };
      }
      if (row.inheritsFrom === null) {
        return { ...row, effectiveValue: row.envValue ?? '0', source: 'env' };
      }
      return {
        ...row,
        effectiveValue: settled.get(row.inheritsFrom) ?? row.effectiveValue,
        source: 'inherited',
      };
    });
  }

  private canWriteTierLimits(role: string): boolean {
    return role === 'compliance' || role === 'superadmin';
  }

  private listTierLimits(role: string): RawResponse {
    if (!this.canWriteTierLimits(role)) {
      return fail(403, 'FORBIDDEN', 'Your role cannot see the tier ceilings.');
    }
    return ok({ tiers: this.tierLimits });
  }

  private setTierLimits(path: string, body: unknown, adminId: string, role: string): RawResponse {
    if (!this.canWriteTierLimits(role)) {
      return fail(403, 'FORBIDDEN', 'Your role cannot change the tier ceilings.');
    }
    const tier = decodeURIComponent(path.slice('/admin/limits/tiers/'.length));
    const index = this.tierLimits.findIndex((row) => row.tier === tier);
    // An unknown segment 404s rather than creating a row for a tier nothing enforces.
    if (index === -1) return fail(404, 'NOT_FOUND', 'No such tier.');

    const b = (body ?? {}) as { single?: MoneyDTO; daily?: MoneyDTO; monthly?: MoneyDTO };
    const ceilings = [b.single, b.daily, b.monthly];
    if (ceilings.some((c) => c === undefined || typeof c.amount !== 'string')) {
      return fail(422, 'VALIDATION_FAILED', 'A tier needs a single, daily and monthly ceiling.');
    }
    if (ceilings.some((c) => !/^-?\d+$/.test(c!.amount))) {
      return fail(422, 'VALIDATION_FAILED', 'amount must be an integer string of minor units');
    }
    // Naira only: every ceiling is compared against a naira-valued outflow, so one
    // denominated in anything else would be compared against a number it does not describe.
    if (ceilings.some((c) => c!.currency !== 'NGN')) {
      return fail(
        422,
        'VALIDATION_FAILED',
        'tier ceilings are naira, and the outflow they are compared against is',
      );
    }
    if (ceilings.some((c) => c!.amount.startsWith('-'))) {
      return fail(422, 'VALIDATION_FAILED', 'a ceiling cannot be negative');
    }
    // `single ≤ daily ≤ monthly`. A daily ceiling below the single-transaction one errors
    // nowhere — it just makes the single ceiling unreachable, so customers hit "limit
    // exceeded" on a transaction the console says is allowed.
    if (BigInt(b.single!.amount) > BigInt(b.daily!.amount)) {
      return fail(422, 'VALIDATION_FAILED', 'the single-transaction ceiling cannot exceed the daily one');
    }
    if (BigInt(b.daily!.amount) > BigInt(b.monthly!.amount)) {
      return fail(422, 'VALIDATION_FAILED', 'the daily ceiling cannot exceed the monthly one');
    }

    const updated: TierLimit = {
      tier: tier as KycTier,
      single: b.single!,
      daily: b.daily!,
      monthly: b.monthly!,
      updatedAt: new Date().toISOString(),
      updatedByAdminId: adminId,
    };
    this.tierLimits[index] = updated;
    return ok(updated);
  }

  // ── KYC: the watchdog, and failover back towards the machine ──

  /**
   * How deep the manual queue is and how old its oldest item is.
   *
   * `stale` is computed here rather than left to the caller, exactly as the API computes it,
   * so the console cannot form its own opinion about what "stale" means and disagree with the
   * threshold the contract publishes.
   */
  private kycPending(): {
    pending: number;
    oldestSubmittedAt: string | null;
    oldestAgeSeconds: number | null;
    staleAfterSeconds: number;
    stale: boolean;
  } {
    let oldest: string | null = null;
    for (const item of this.kyc) {
      if (oldest === null || item.submittedAt < oldest) oldest = item.submittedAt;
    }
    const ageSeconds =
      oldest === null ? null : Math.max(0, Math.floor((Date.now() - Date.parse(oldest)) / 1000));
    return {
      pending: this.kyc.length,
      oldestSubmittedAt: oldest,
      oldestAgeSeconds: ageSeconds,
      staleAfterSeconds: KYC_SLA_SECONDS,
      stale: ageSeconds !== null && ageSeconds > KYC_SLA_SECONDS,
    };
  }

  /**
   * Re-run the automated check on a queued submission.
   *
   * The outcome is derived from what the chain said last time rather than drawn at random,
   * because the point of the demo is to show the two paths that differ: an item that failed
   * because *no vendor answered* (`unsupported`/`error`) is the one a re-run plausibly
   * rescues once the rail is fixed, while a `mismatch` is a human judgement that re-running
   * will not change. On a pass the submission leaves the queue, exactly as it does in the
   * API — so the console must reload rather than assume the item is still there.
   */
  private recheckKyc(path: string, role: string): RawResponse {
    if (role !== 'compliance' && role !== 'superadmin') {
      return fail(403, 'FORBIDDEN', 'Your role cannot re-run identity checks.');
    }
    const id = decodeURIComponent(
      path.slice('/admin/kyc/queue/'.length, path.length - '/recheck'.length),
    );
    const index = this.kyc.findIndex((item) => item.id === id);
    if (index === -1) return fail(404, 'NOT_FOUND', 'That submission is no longer pending.');

    const item = this.kyc[index]!;
    const checkedAt = new Date().toISOString();
    const rescued = item.providerOutcome === 'unsupported' || item.providerOutcome === 'error';
    const outcome = rescued ? 'verified' : item.providerOutcome ?? 'error';
    const providerSlug = rescued ? 'dojah' : item.providerSlug;
    const providerReference = `JOB-${String(Date.now()).slice(-8)}`;

    if (outcome === 'verified') {
      // The re-run approved it on the vendor's word, so it is gone from the queue.
      this.kyc.splice(index, 1);
      return ok({
        submissionId: id,
        status: 'verified',
        decidedBy: 'auto',
        outcome,
        providerSlug,
        providerReference,
        reason: null,
        checkedAt,
      });
    }

    // Still undecided: the item stays where it is, with fresher provenance on it.
    this.kyc[index] = {
      ...item,
      providerOutcome: outcome,
      providerSlug,
      providerReference,
      autoCheckedAt: checkedAt,
    };
    return ok({
      submissionId: id,
      status: 'pending',
      decidedBy: null,
      outcome,
      providerSlug,
      providerReference,
      reason:
        outcome === 'mismatch'
          ? 'The name on the record does not match this account.'
          : outcome === 'not_found'
            ? 'No record was found for that number.'
            : null,
      checkedAt,
    });
  }

  // ── bank directory ──
  private bankDirectory(): { banks: AdminBank[]; lastSyncedAt: string | null } {
    let lastSyncedAt: string | null = null;
    for (const bank of this.banks) {
      if (bank.syncedAt !== null && (lastSyncedAt === null || bank.syncedAt > lastSyncedAt)) {
        lastSyncedAt = bank.syncedAt;
      }
    }
    return { banks: this.banks, lastSyncedAt };
  }

  /**
   * The mock stores no bytes, so it records only that a logo now exists — which is what the
   * page renders off. `logoPath` is still set so the row flips out of its "missing" state;
   * the page knows not to fetch it against the in-memory backend.
   */
  private uploadBankLogo(path: string, body: unknown, adminId: string): RawResponse {
    const id = decodeURIComponent(path.slice('/admin/banks/'.length, path.length - '/logo'.length));
    const index = this.banks.findIndex((b) => b.id === id);
    if (index === -1) return fail(404, 'NOT_FOUND', 'No such bank.');

    const b = (body ?? {}) as { mime?: string; dataBase64?: string };
    if (typeof b.mime !== 'string' || typeof b.dataBase64 !== 'string' || b.dataBase64.length === 0) {
      return fail(422, 'VALIDATION_FAILED', 'A logo image is required.');
    }

    const current = this.banks[index]!;
    const now = new Date().toISOString();
    const updated: AdminBank = {
      ...current,
      logoMime: b.mime as AdminBank['logoMime'],
      logoPath: `/banks/${current.id}/logo?v=${Date.parse(now)}`,
      logoUpdatedAt: now,
      logoUpdatedByAdminId: adminId,
    };
    this.banks[index] = updated;
    return ok(updated);
  }

  // ── home-screen banners ──
  /**
   * The demo's stand-in for the server's scheduling rule.
   *
   * It is duplicated here rather than imported because the console holds no runtime
   * dependency on the contracts package, and the real answer comes from the API anyway —
   * this only has to keep the offline demo honest, so that a banner saved with a past end
   * date shows as finished rather than as running.
   */
  private isLive(banner: Pick<AdminBanner, 'active' | 'startsAt' | 'endsAt'>): boolean {
    if (!banner.active) return false;
    const now = Date.now();
    if (banner.startsAt !== null && Date.parse(banner.startsAt) > now) return false;
    if (banner.endsAt !== null && Date.parse(banner.endsAt) <= now) return false;
    return true;
  }

  private bannerIdFrom(path: string, suffix = ''): string {
    const tail = path.slice('/admin/banners/'.length);
    return decodeURIComponent(suffix === '' ? tail : tail.slice(0, tail.length - suffix.length));
  }

  private createBanner(body: unknown, adminId: string): RawResponse {
    const b = (body ?? {}) as Partial<AdminBanner>;
    if (typeof b.title !== 'string' || b.title.trim() === '' || typeof b.deepLink !== 'string') {
      return fail(422, 'VALIDATION_FAILED', 'A banner needs a title and a destination.');
    }
    const now = new Date().toISOString();
    this.bannerSeq += 1;
    const draft: AdminBanner = {
      id: `a0000000-0000-4000-8000-${String(this.bannerSeq).padStart(12, '0')}`,
      title: b.title,
      body: b.body ?? null,
      deepLink: b.deepLink,
      // The real API defaults these from the contract's preset when a client omits them, so a
      // banner is never colourless. The mock does the same, or the demo console would render
      // a preview of `undefined`.
      backgroundColor: b.backgroundColor ?? '#1652F0',
      textColor: b.textColor ?? '#FFFFFF',
      externalUrl: b.externalUrl ?? null,
      imageMime: null,
      imagePath: null,
      active: b.active ?? true,
      sortOrder: b.sortOrder ?? 0,
      startsAt: b.startsAt ?? null,
      endsAt: b.endsAt ?? null,
      live: false,
      updatedByAdminId: adminId,
      createdAt: now,
      updatedAt: now,
    };
    const created: AdminBanner = { ...draft, live: this.isLive(draft) };
    this.banners = [...this.banners, created].sort((x, y) => x.sortOrder - y.sortOrder);
    return ok(created, 201);
  }

  private updateBanner(path: string, body: unknown, adminId: string): RawResponse {
    const id = this.bannerIdFrom(path);
    const index = this.banners.findIndex((x) => x.id === id);
    if (index === -1) return fail(404, 'NOT_FOUND', 'No such banner.');

    const patch = (body ?? {}) as Partial<AdminBanner>;
    const current = this.banners[index]!;
    // Absent means "leave it alone", exactly as the API's PATCH does — so a reorder that
    // sends only `sortOrder` cannot blank the copy.
    const merged: AdminBanner = {
      ...current,
      ...(patch.title === undefined ? {} : { title: patch.title }),
      ...(patch.body === undefined ? {} : { body: patch.body }),
      ...(patch.deepLink === undefined ? {} : { deepLink: patch.deepLink }),
      ...(patch.backgroundColor === undefined ? {} : { backgroundColor: patch.backgroundColor }),
      ...(patch.textColor === undefined ? {} : { textColor: patch.textColor }),
      ...(patch.externalUrl === undefined ? {} : { externalUrl: patch.externalUrl }),
      ...(patch.active === undefined ? {} : { active: patch.active }),
      ...(patch.sortOrder === undefined ? {} : { sortOrder: patch.sortOrder }),
      ...(patch.startsAt === undefined ? {} : { startsAt: patch.startsAt }),
      ...(patch.endsAt === undefined ? {} : { endsAt: patch.endsAt }),
      updatedByAdminId: adminId,
      updatedAt: new Date().toISOString(),
    };
    const updated: AdminBanner = { ...merged, live: this.isLive(merged) };
    this.banners = this.banners
      .map((x) => (x.id === id ? updated : x))
      .sort((x, y) => x.sortOrder - y.sortOrder);
    return ok(updated);
  }

  private deleteBanner(path: string): RawResponse {
    const id = this.bannerIdFrom(path);
    if (!this.banners.some((x) => x.id === id)) return fail(404, 'NOT_FOUND', 'No such banner.');
    this.banners = this.banners.filter((x) => x.id !== id);
    return { status: 204, ok: true, json: null };
  }

  /**
   * The mock stores no bytes, so it records only that artwork now exists — which is what
   * the page renders off. `imagePath` is still set so the row leaves its "no image" state;
   * the page knows not to fetch it against the in-memory backend.
   */
  private uploadBannerImage(path: string, body: unknown, adminId: string): RawResponse {
    const id = this.bannerIdFrom(path, '/image');
    const index = this.banners.findIndex((x) => x.id === id);
    if (index === -1) return fail(404, 'NOT_FOUND', 'No such banner.');

    const b = (body ?? {}) as { mime?: string; dataBase64?: string };
    if (typeof b.mime !== 'string' || typeof b.dataBase64 !== 'string' || b.dataBase64.length === 0) {
      return fail(422, 'VALIDATION_FAILED', 'A banner image is required.');
    }

    const now = new Date().toISOString();
    const updated: AdminBanner = {
      ...this.banners[index]!,
      imageMime: b.mime as AdminBanner['imageMime'],
      imagePath: `/banners/${id}/image?v=${Date.parse(now)}`,
      updatedByAdminId: adminId,
      updatedAt: now,
    };
    this.banners[index] = updated;
    return ok(updated);
  }

  // ── gift-card brand catalogue ──
  //
  // The catalogue is a *finance* surface, not a compliance one, and everything below is
  // written to that: the list, the create and the patch all refuse a role without
  // `rates:write` with the same 403 the API returns. The list especially — a mock that
  // served the catalogue to any admin would make a compliance operator's demo work and
  // their production console error-box on load.

  /** Finance and superadmin, exactly as `BRAND_WRITE_ROLES` admits. */
  private canWriteBrands(role: string): boolean {
    return role === 'finance' || role === 'superadmin';
  }

  private listBrands(role: string): RawResponse {
    if (!this.canWriteBrands(role)) {
      return fail(403, 'FORBIDDEN', 'Your role cannot see or set gift-card rates.');
    }
    // Sorted the way the API orders it: `sortOrder` then name, so the console never has to
    // guess at display order and a reorder in the demo behaves like a reorder in production.
    const brands = [...this.giftcardBrands].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
    );
    return ok({ brands });
  }

  private createBrand(body: unknown, role: string): RawResponse {
    if (!this.canWriteBrands(role)) {
      return fail(403, 'FORBIDDEN', 'Your role cannot create gift-card brands.');
    }
    const b = (body ?? {}) as Partial<CreateGiftcardBrandRequest>;

    const invalid = validateBrandFields(b, {
      name: b.name,
      category: b.category,
      country: b.country,
      faceCurrency: b.faceCurrency,
      rate: b.rateNairaPerUnit,
      minFace: b.minFace,
      maxFace: b.maxFace,
      sortOrder: b.sortOrder,
      required: true,
    });
    if (invalid !== null) return invalid;
    if (b.logoKey !== undefined && b.logoKey !== null && b.logoKey !== this.brand?.key) {
      return fail(422, 'VALIDATION_FAILED', 'logoKey: no such brand asset');
    }

    // No deviation guard on create: a first rate has nothing to deviate from, which is also
    // the deliberate two-write escape hatch for a genuine ten-fold repricing.
    const rate = giftcardRational(b.rateNairaPerUnit as string);
    if (rate === null) return fail(422, 'VALIDATION_FAILED', 'rateNairaPerUnit: rate must be greater than zero');

    this.brandSeq += 1;
    const brand: AdminGiftcardBrand = {
      id: `gb000000-0000-4000-8000-${String(900000 + this.brandSeq).padStart(12, '0')}`,
      name: (b.name as string).trim(),
      category: b.category as AdminGiftcardBrand['category'],
      country: b.country as string,
      faceCurrency: b.faceCurrency as AdminGiftcardBrand['faceCurrency'],
      rateNairaPerUnit: rate,
      minFace: b.minFace as AdminGiftcardBrand['minFace'],
      maxFace: b.maxFace as AdminGiftcardBrand['maxFace'],
      // The contract's defaults: a new brand is live and sorts at 0 unless said otherwise.
      active: b.active ?? true,
      logoKey: b.logoKey ?? null,
      sortOrder: b.sortOrder ?? 0,
      createdAt: new Date().toISOString(),
    };
    this.giftcardBrands.push(brand);
    // 201, not 200 — the API answers `201 Created` here and a console that only handled
    // 200 would treat a successful create as a failure.
    return ok(brand, 201);
  }

  private updateBrand(path: string, body: unknown, role: string): RawResponse {
    if (!this.canWriteBrands(role)) {
      return fail(403, 'FORBIDDEN', 'Your role cannot change gift-card rates.');
    }
    const id = decodeURIComponent(path.slice('/admin/giftcards/brands/'.length));
    const index = this.giftcardBrands.findIndex((brand) => brand.id === id);
    if (index === -1) return fail(404, 'NOT_FOUND', 'No such brand.');

    const current = this.giftcardBrands[index]!;
    const b = (body ?? {}) as Partial<UpdateGiftcardBrandRequest>;

    // Every check runs against the *merged* result, not the patch — the only way a
    // single-field edit can be safe. Changing `faceCurrency` alone has to re-validate
    // bounds that are not in the request at all, or a USD brand ends up carrying EUR limits.
    const faceCurrency = b.faceCurrency ?? current.faceCurrency;
    const minFace = b.minFace ?? { ...current.minFace, currency: faceCurrency };
    const maxFace = b.maxFace ?? { ...current.maxFace, currency: faceCurrency };
    const invalid = validateBrandFields(b, {
      name: b.name ?? current.name,
      category: b.category ?? current.category,
      country: b.country ?? current.country,
      faceCurrency,
      rate: b.rateNairaPerUnit,
      minFace,
      maxFace,
      sortOrder: b.sortOrder ?? current.sortOrder,
      required: false,
    });
    if (invalid !== null) return invalid;
    if (b.logoKey !== undefined && b.logoKey !== null && b.logoKey !== this.brand?.key) {
      return fail(422, 'VALIDATION_FAILED', 'logoKey: no such brand asset');
    }

    let rate = current.rateNairaPerUnit;
    if (b.rateNairaPerUnit !== undefined) {
      const next = giftcardRational(b.rateNairaPerUnit);
      if (next === null) {
        return fail(422, 'VALIDATION_FAILED', 'rateNairaPerUnit: rate must be greater than zero');
      }
      // The deviation guard, anchored on the brand's own standing rate and **exclusive** at
      // ten times, because a misplaced decimal point is precisely 10× and a guard that
      // allowed the boundary would miss its own headline case.
      const previous = { numerator: BigInt(current.rateNairaPerUnit.numerator), denominator: BigInt(current.rateNairaPerUnit.denominator) };
      const proposed = { numerator: BigInt(next.numerator), denominator: BigInt(next.denominator) };
      const nextOverPrev = proposed.numerator * previous.denominator;
      const prevOverNext = previous.numerator * proposed.denominator;
      if (nextOverPrev >= prevOverNext * 10n || nextOverPrev * 10n <= prevOverNext) {
        return fail(
          422,
          'VALIDATION_FAILED',
          'rateNairaPerUnit: that is 10× or more away from the current rate;' +
            ' re-enter it, or move it in smaller steps if the change is intended',
        );
      }
      rate = next;
    }

    const updated: AdminGiftcardBrand = {
      ...current,
      name: (b.name ?? current.name).trim(),
      category: b.category ?? current.category,
      country: b.country ?? current.country,
      faceCurrency,
      rateNairaPerUnit: rate,
      minFace,
      maxFace,
      active: b.active ?? current.active,
      // `undefined` means "not in the patch"; an explicit `null` clears the logo.
      logoKey: b.logoKey === undefined ? current.logoKey : b.logoKey,
      sortOrder: b.sortOrder ?? current.sortOrder,
    };
    this.giftcardBrands[index] = updated;
    return ok(updated);
  }

  // ── gift cards ──
  private giftcardQueue(): RawResponse {
    const items = this.redemptions
      .filter((r) => r.status === 'pending')
      .map(({ id, maskedPhone, brandName, faceValue, quantity, payoutAmount, status, createdAt }) => ({
        id,
        maskedPhone,
        brandName,
        faceValue,
        quantity,
        payoutAmount,
        status,
        createdAt,
      }));
    return ok({ items, nextCursor: null });
  }

  private getRedemption(path: string): RawResponse {
    const id = decodeURIComponent(path.slice('/admin/giftcards/queue/'.length));
    const detail = this.redemptions.find((r) => r.id === id);
    return detail === undefined ? fail(404, 'NOT_FOUND', 'No such redemption.') : ok(detail);
  }

  private reviewGiftcard(path: string, body: unknown, adminId: string): RawResponse {
    const id = decodeURIComponent(
      path.slice('/admin/giftcards/queue/'.length, path.length - '/review'.length),
    );
    const idx = this.redemptions.findIndex((r) => r.id === id);
    if (idx === -1) return fail(404, 'NOT_FOUND', 'No such redemption.');
    const current = this.redemptions[idx]!;
    if (current.status !== 'pending') return fail(409, 'CONFLICT', 'This redemption was already reviewed.');
    const decision = (body as { decision?: string } | null)?.decision;
    if (decision !== 'approve' && decision !== 'reject') {
      return fail(422, 'VALIDATION_FAILED', 'A decision is required.');
    }
    const reason = (body as { reason?: string } | null)?.reason ?? null;
    if (decision === 'reject' && (reason === null || reason.trim() === '')) {
      return fail(422, 'VALIDATION_FAILED', 'A rejection needs a reason.');
    }
    const updated: AdminRedemptionDetail = {
      ...current,
      status: decision === 'approve' ? 'approved' : 'rejected',
      reason: decision === 'reject' ? reason : null,
      reviewedAt: new Date().toISOString(),
      reviewedByAdminId: adminId,
    };
    this.redemptions[idx] = updated;
    return ok(updated);
  }

  // ── support ──
  /** Project a stored thread to an inbox/detail row (drop the message log). */
  private rowOf(t: MockSupportThread): AdminThreadRow {
    const { messages: _m, ...row } = t;
    return row;
  }

  /** Attach the server-derived `mine` (true when the operator/admin authored the message). */
  private viewMessages(t: MockSupportThread): Message[] {
    return t.messages.map((m: MockMessage) => ({ ...m, mine: m.senderType === 'admin' }));
  }

  private supportInbox(query?: RawRequest['query']): RawResponse {
    const status = query?.['status'];
    const priority = query?.['priority'];
    const assignedAdminId = query?.['assignedAdminId'];
    const items = this.supportThreads
      .filter(
        (t) =>
          (status === undefined || t.status === status) &&
          (priority === undefined || t.priority === priority) &&
          (assignedAdminId === undefined || t.assignedAdminId === assignedAdminId),
      )
      // Newest activity first, matching the real keyset order.
      .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt))
      .map((t) => this.rowOf(t));
    return ok({ items, nextCursor: null });
  }

  private threadIdFrom(path: string, suffix = ''): string {
    const rest = path.slice('/admin/support/threads/'.length);
    const raw = suffix !== '' && rest.endsWith(suffix) ? rest.slice(0, rest.length - suffix.length) : rest;
    return decodeURIComponent(raw);
  }

  private supportThread(path: string): RawResponse {
    const id = this.threadIdFrom(path);
    const t = this.supportThreads.find((x) => x.id === id);
    if (t === undefined) return fail(404, 'NOT_FOUND', 'That conversation no longer exists.');
    // Opening the thread clears the operator-side unread count.
    t.unreadForAdmin = 0;
    return ok({ thread: this.rowOf(t), messages: this.viewMessages(t), nextCursor: null });
  }

  private supportReply(path: string, body: unknown, adminId: string): RawResponse {
    const id = this.threadIdFrom(path, '/reply');
    const t = this.supportThreads.find((x) => x.id === id);
    if (t === undefined) return fail(404, 'NOT_FOUND', 'That conversation no longer exists.');
    if (t.status === 'closed') return fail(409, 'THREAD_CLOSED', 'This conversation is closed.');

    const b = (body ?? {}) as {
      body?: string;
      status?: string;
      attachment?: { data?: string; mime?: string; name?: string };
    };
    const text = typeof b.body === 'string' ? b.body.trim() : '';
    const hasAttachment =
      b.attachment !== undefined && typeof b.attachment.data === 'string' && b.attachment.data.length > 0;
    if (text === '' && !hasAttachment) {
      return fail(422, 'VALIDATION_FAILED', 'A reply needs a body or an attachment.');
    }

    const attachments: Attachment[] = [];
    if (hasAttachment) {
      const a = b.attachment!;
      const attId = `at000000-0000-4000-8000-${String(this.supportSeq++).padStart(12, '0')}`;
      const mime = a.mime ?? 'application/octet-stream';
      const name = a.name ?? 'attachment';
      const data = a.data!;
      this.supportMedia[attId] = { mime, name, dataBase64: data };
      const clean = data.replace(/=+$/, '');
      attachments.push({ id: attId, mime, name, size: Math.floor((clean.length * 3) / 4) });
    }

    const now = new Date().toISOString();
    const message: MockMessage = {
      id: `ms000000-0000-4000-8000-${String(this.supportSeq++).padStart(12, '0')}`,
      threadId: t.id,
      senderType: 'admin',
      body: text === '' ? null : text,
      attachments,
      createdAt: now,
    };
    t.messages.push(message);
    t.lastMessageAt = now;
    t.unreadForAdmin = 0;
    // A reply from an unassigned thread claims it for the replying operator.
    if (t.assignedAdminId === null) t.assignedAdminId = adminId;
    if (b.status === 'open' || b.status === 'pending' || b.status === 'resolved' || b.status === 'closed') {
      t.status = b.status;
    } else if (t.status === 'resolved') {
      // A fresh reply reopens a resolved thread unless the caller set a status explicitly.
      t.status = 'open';
    }
    return ok({ thread: this.rowOf(t), messages: this.viewMessages(t), nextCursor: null });
  }

  private supportAssign(path: string, body: unknown): RawResponse {
    const id = this.threadIdFrom(path, '/assign');
    const t = this.supportThreads.find((x) => x.id === id);
    if (t === undefined) return fail(404, 'NOT_FOUND', 'That conversation no longer exists.');
    const b = (body ?? {}) as { assignedAdminId?: string | null; priority?: string };
    if (!('assignedAdminId' in (b as object))) {
      return fail(422, 'VALIDATION_FAILED', 'An assignee (or null) is required.');
    }
    t.assignedAdminId = b.assignedAdminId ?? null;
    if (b.priority === 'low' || b.priority === 'normal' || b.priority === 'high' || b.priority === 'urgent') {
      t.priority = b.priority;
    }
    return ok(this.rowOf(t));
  }

  private setSupportSettings(body: unknown): RawResponse {
    const b = (body ?? {}) as Partial<SupportSettings>;
    const next: SupportSettings = {
      supportEmail: typeof b.supportEmail === 'string' ? b.supportEmail : this.supportSettings.supportEmail,
      autoReplyEnabled:
        typeof b.autoReplyEnabled === 'boolean' ? b.autoReplyEnabled : this.supportSettings.autoReplyEnabled,
      autoReplyMessage:
        typeof b.autoReplyMessage === 'string' ? b.autoReplyMessage : this.supportSettings.autoReplyMessage,
      businessHours: b.businessHours ?? this.supportSettings.businessHours,
    };
    this.supportSettings = next;
    return ok(next);
  }

  private getSupportMedia(path: string): RawResponse {
    const id = decodeURIComponent(path.slice('/admin/support/media/'.length));
    const media = this.supportMedia[id];
    if (media === undefined) return fail(404, 'NOT_FOUND', 'That attachment is no longer available.');
    return ok({ id, mime: media.mime, name: media.name, dataBase64: media.dataBase64 });
  }

  // ── Business Settings ──
  private setBusinessSettings(body: unknown, adminId: string): RawResponse {
    const b = (body ?? {}) as SetPlatformSettingsRequest;
    const cur = this.platformSettings;
    const next: PlatformSettings = {
      platformName: b.platformName ?? cur.platformName,
      // Carried through unchanged: the text-colour choice has its own route, so a settings
      // save must not be able to reset it as a side effect.
      heroNairaTextColor: cur.heroNairaTextColor,
      heroCryptoTextColor: cur.heroCryptoTextColor,
      logoAssetKey: b.logoAssetKey !== undefined ? b.logoAssetKey : cur.logoAssetKey,
      splashAssetKey: b.splashAssetKey !== undefined ? b.splashAssetKey : cur.splashAssetKey,
      iconAssetKey: b.iconAssetKey !== undefined ? b.iconAssetKey : cur.iconAssetKey,
      heroNairaAssetKey:
        b.heroNairaAssetKey !== undefined ? b.heroNairaAssetKey : cur.heroNairaAssetKey,
      heroCryptoAssetKey:
        b.heroCryptoAssetKey !== undefined ? b.heroCryptoAssetKey : cur.heroCryptoAssetKey,
      primaryColor: b.primaryColor ?? cur.primaryColor,
      accentColor: b.accentColor ?? cur.accentColor,
      supportEmail: b.supportEmail ?? cur.supportEmail,
      contactEmail: b.contactEmail ?? cur.contactEmail,
      // Nullable, and a `null` an operator deliberately sent has to survive the patch: the
      // `??` form above would silently restore the old address, which on an alert channel
      // means mail still going to somebody who thought they had turned it off.
      adminNotifyEmail:
        b.adminNotifyEmail !== undefined ? b.adminNotifyEmail : cur.adminNotifyEmail,
      deviceOtpEmailEnabled: b.deviceOtpEmailEnabled ?? cur.deviceOtpEmailEnabled,
      deviceOtpSmsEnabled: b.deviceOtpSmsEnabled ?? cur.deviceOtpSmsEnabled,
      websiteUrl: b.websiteUrl !== undefined ? b.websiteUrl : cur.websiteUrl,
      termsUrl: b.termsUrl !== undefined ? b.termsUrl : cur.termsUrl,
      privacyUrl: b.privacyUrl !== undefined ? b.privacyUrl : cur.privacyUrl,
      social: b.social ?? cur.social,
      defaultCurrency: b.defaultCurrency ?? cur.defaultCurrency,
      conversionCurrencies: b.conversionCurrencies ?? cur.conversionCurrencies,
      updatedByAdminId: adminId,
      updatedAt: new Date().toISOString(),
    };
    this.platformSettings = next;
    return ok(next);
  }

  // ── legal & about documents ──
  /** The three keys the API knows. A fourth is a 404 there, so it is a 404 here too. */
  private publishLegalDocument(path: string, body: unknown, adminId: string): RawResponse {
    const key = decodeURIComponent(path.slice('/admin/legal/'.length));
    if (!['terms', 'privacy', 'about'].includes(key)) return fail(404, 'NOT_FOUND', 'No such document.');

    const b = (body ?? {}) as { title?: string; body?: string };
    if (typeof b.title !== 'string' || b.title.trim() === '' || typeof b.body !== 'string' || b.body.trim() === '') {
      return fail(422, 'VALIDATION_FAILED', 'A document needs a title and some text.');
    }

    const next: AdminLegalDocument = {
      key: key as AdminLegalDocument['key'],
      title: b.title.trim(),
      body: b.body.trim(),
      updatedByAdminId: adminId,
      updatedAt: new Date().toISOString(),
    };
    const idx = this.legalDocuments.findIndex((d) => d.key === key);
    // A document nobody has published yet does not exist as a row, so a publish creates one.
    if (idx === -1) this.legalDocuments.push(next);
    else this.legalDocuments[idx] = next;
    return ok(next);
  }

  // ── email templates ──
  private emailKeyFrom(path: string, suffix = ''): string {
    const rest = path.slice('/admin/email-templates/'.length);
    const raw = suffix !== '' && rest.endsWith(suffix) ? rest.slice(0, rest.length - suffix.length) : rest;
    return decodeURIComponent(raw);
  }

  private upsertEmailTemplate(path: string, body: unknown, adminId: string): RawResponse {
    const key = this.emailKeyFrom(path);
    const idx = this.emailTemplates.findIndex((t) => t.key === key);
    if (idx === -1) return fail(404, 'NOT_FOUND', 'No such template.');
    const b = (body ?? {}) as Partial<EmailTemplate>;
    if (typeof b.name !== 'string' || typeof b.subject !== 'string' || typeof b.html !== 'string') {
      return fail(422, 'VALIDATION_FAILED', 'A template needs a name, subject and HTML.');
    }
    const cur = this.emailTemplates[idx]!;
    const next: EmailTemplate = {
      ...cur,
      name: b.name,
      subject: b.subject,
      preheader: typeof b.preheader === 'string' ? b.preheader.trim().slice(0, 200) : cur.preheader,
      html: b.html,
      /*
       * An absent `usesLayout` means "leave it as it is", never "make this a whole document".
       *
       * The distinction bites on the first save after the layout shipped. A console that sent
       * `usesLayout: false` by omission would silently turn a layout-based template back into
       * a bare fragment rendered with no chrome, and the operator would find out by looking at
       * their own inbox. The contract says this in as many words; the mock honours it so the
       * console can never be written against the wrong rule and pass here.
       */
      usesLayout: typeof b.usesLayout === 'boolean' ? b.usesLayout : cur.usesLayout,
      variables: Array.isArray(b.variables) ? b.variables : cur.variables,
      enabled: typeof b.enabled === 'boolean' ? b.enabled : cur.enabled,
      updatedByAdminId: adminId,
      updatedAt: new Date().toISOString(),
    };
    this.emailTemplates[idx] = next;
    return ok(next);
  }

  /**
   * Put one template back to the shipped default.
   *
   * The default is read from the seed rather than from a snapshot taken at construction, so a
   * restore returns what the catalogue actually ships rather than whatever this tab happened
   * to start the session with.
   */
  private restoreEmailTemplate(path: string, adminId: string): RawResponse {
    const key = this.emailKeyFrom(path, '/restore');
    const idx = this.emailTemplates.findIndex((t) => t.key === key);
    const shipped = seedEmailTemplates().find((t) => t.key === key);
    if (idx === -1 || shipped === undefined) return fail(404, 'NOT_FOUND', 'No such template.');
    const next: EmailTemplate = {
      ...shipped,
      updatedByAdminId: adminId,
      updatedAt: new Date().toISOString(),
    };
    this.emailTemplates[idx] = next;
    return ok(next);
  }

  // ── rendering ──

  /** In the mock a configured logo resolves to a placeholder image; an unset one to nothing. */
  private mockLogoUrl(): string | null {
    return this.platformSettings.logoAssetKey !== null ? API_MEDIA_LOGO : null;
  }

  /** The identity half of a render: who is sending, as opposed to how it looks. */
  private renderBrand(): {
    platformName: string;
    logoUrl: string | null;
    supportEmail: string;
    websiteUrl: string | null;
  } {
    const s = this.platformSettings;
    return {
      platformName: s.platformName,
      logoUrl: this.mockLogoUrl(),
      supportEmail: s.supportEmail,
      websiteUrl: s.websiteUrl,
    };
  }

  /**
   * Everything a `{{token}}` can resolve to.
   *
   * The globals go on last, so they win over a caller's sample vars. That ordering is the
   * server's and it is the point of them being globals: a variable map must not be able to
   * repaint the brand, move the logo or rewrite the support address.
   */
  private emailVars(sampleVars: Record<string, string> | undefined): Record<string, string> {
    const globals = emailTokens(
      this.platformSettings,
      this.activeEmailLayout().content,
      this.mockLogoUrl(),
    );
    return { ...SAMPLE_VARS, ...(sampleVars ?? {}), ...globals };
  }

  /**
   * One rendered message, in the shape `renderPreviewResponseSchema` publishes.
   *
   * `html` is the finished document including the layout's chrome rather than the template's
   * fragment, because a preview of the fragment previews something no customer will ever
   * receive. `text` is the plain-text alternative that rides alongside it in the multipart
   * message: it is what plain-text clients render and what several spam filters score, and it
   * is the half nobody looks at until it is wrong.
   */
  private renderPreview(
    source: { subject: string; preheader: string; html: string; usesLayout: boolean },
    sampleVars: Record<string, string> | undefined,
  ): RenderPreviewResponse {
    const missing = new Set<string>();
    const vars = this.emailVars(sampleVars);
    const subject = substitute(source.subject, vars, missing);
    const preheader = substitute(source.preheader, vars, missing);
    const body = substitute(source.html, vars, missing);
    const layout = this.activeEmailLayout().content;
    const brand = this.renderBrand();
    const html = source.usesLayout
      ? renderWithLayout(layout, brand, { preheader, bodyHtml: body })
      : body;
    return {
      subject,
      preheader,
      html,
      /*
       * The text leg is built from the same pieces as the HTML rather than run over the
       * finished document, which is the same choice the API's renderer makes and for the same
       * two reasons.
       *
       * The preheader block ends in thirty repetitions of zero-width filler, put there so an
       * inbox stops reading into the body. Converting the whole document drags all of it into
       * the text part, where it is a wall of invisible characters under the first line. And a
       * footer flattened out of the finished HTML keeps the link *labels* and loses the
       * addresses, so the text leg reads "Terms" with nowhere to go.
       *
       * `htmlToPlainText` still does the markup-to-text step; it is just handed the body
       * rather than the document.
       */
      text: source.usesLayout
        ? emailPlainText(layout, brand, preheader, body)
        : htmlToPlainText(html).trim(),
      missingVariables: [...missing],
    };
  }

  private previewEmailTemplate(path: string, body: unknown): RawResponse {
    const key = this.emailKeyFrom(path, '/preview');
    const t = this.emailTemplates.find((x) => x.key === key);
    if (t === undefined) return fail(404, 'NOT_FOUND', 'No such template.');
    const sampleVars = ((body ?? {}) as { sampleVars?: Record<string, string> }).sampleVars;
    return ok(this.renderPreview(t, sampleVars));
  }

  private testSendEmailTemplate(path: string, body: unknown): RawResponse {
    const key = this.emailKeyFrom(path, '/test-send');
    const t = this.emailTemplates.find((x) => x.key === key);
    if (t === undefined) return fail(404, 'NOT_FOUND', 'No such template.');
    const to = ((body ?? {}) as { to?: string }).to ?? '';
    if (!EMAIL_RE.test(to.trim())) {
      return fail(422, 'VALIDATION_FAILED', 'A valid recipient email is required.');
    }
    return ok({ ok: true, to });
  }

  // ── the email layout ──

  /**
   * The live layout.
   *
   * Always the newest entry, because writes are append-only and a restore copies an old
   * version forward rather than rewinding the pointer. There is always at least one: the seed
   * puts version 1 there and nothing removes it.
   */
  private activeEmailLayout(): EmailLayout {
    return this.emailLayouts[this.emailLayouts.length - 1]!;
  }

  /** The history, newest first, with `active` on the one that is currently in force. */
  private emailLayoutVersions(): EmailLayoutVersion[] {
    const active = this.activeEmailLayout().version;
    return this.emailLayouts
      .map((layout) => ({
        version: layout.version,
        active: layout.version === active,
        updatedByAdminId: layout.updatedByAdminId,
        updatedAt: layout.updatedAt,
      }))
      .sort((a, b) => b.version - a.version);
  }

  private setEmailLayout(body: unknown, adminId: string): RawResponse {
    const parsed = readEmailLayoutContent(body);
    if ('error' in parsed) return fail(422, 'VALIDATION_FAILED', parsed.error);
    const next: EmailLayout = {
      version: this.activeEmailLayout().version + 1,
      content: parsed.content,
      updatedByAdminId: adminId,
      updatedAt: new Date().toISOString(),
    };
    this.emailLayouts.push(next);
    return ok(next);
  }

  /**
   * Restore an earlier version by copying it forward as a new one.
   *
   * Copied rather than rewound, so nothing is ever removed from the history and an operator
   * who restores the wrong version can restore the right one straight afterwards.
   */
  private restoreEmailLayout(body: unknown, adminId: string): RawResponse {
    const wanted = ((body ?? {}) as { version?: unknown }).version;
    if (typeof wanted !== 'number' || !Number.isInteger(wanted) || wanted < 1) {
      return fail(422, 'VALIDATION_FAILED', 'Say which version number to restore.');
    }
    const source = this.emailLayouts.find((layout) => layout.version === wanted);
    if (source === undefined) return fail(404, 'NOT_FOUND', 'There is no such layout version.');
    const next: EmailLayout = {
      version: this.activeEmailLayout().version + 1,
      content: source.content,
      updatedByAdminId: adminId,
      updatedAt: new Date().toISOString(),
    };
    this.emailLayouts.push(next);
    return ok(next);
  }

  /**
   * A short sample message poured through the live layout.
   *
   * The body is fixed rather than taken from the request, because this route exists to show
   * what the *chrome* does: a caller-supplied body would make it a second template preview
   * with no template behind it. The request carries sample vars and nothing else, which is
   * exactly what `RenderPreviewRequest` publishes.
   */
  private previewEmailLayout(body: unknown): RawResponse {
    const sampleVars = ((body ?? {}) as { sampleVars?: Record<string, string> }).sampleVars;
    return ok(this.renderPreview(LAYOUT_PREVIEW_MESSAGE, sampleVars));
  }

  // ── the image library ──

  /**
   * A stable uuid-shaped id.
   *
   * Counted rather than randomised, so two reloads of the demo mint the same ids and a link
   * to a broadcast still resolves after a refresh. `b` and `e` are both hex digits, so these
   * read as real uuids rather than as something a parser will choke on.
   */
  private nextEmailId(prefix: 'b' | 'e'): string {
    const n = this.emailIdSeq;
    this.emailIdSeq += 1;
    return `${prefix}2000000-0000-4000-8000-${String(n).padStart(12, '0')}`;
  }

  private uploadEmailImage(body: unknown, adminId: string): RawResponse {
    const b = (body ?? {}) as { dataBase64?: unknown; label?: unknown; altText?: unknown };

    // Alt text first, and required rather than defaulted. Most clients block images by
    // default, so for a large share of recipients the alt text *is* the image, and the only
    // default available is a blank rectangle in every inbox.
    const altText = typeof b.altText === 'string' ? b.altText.trim() : '';
    if (altText === '') {
      return fail(
        422,
        'VALIDATION_FAILED',
        'Describe the image, so people whose mail client blocks it still get the message.',
      );
    }
    if (altText.length > 200) {
      return fail(422, 'VALIDATION_FAILED', 'Keep the description under 200 characters.');
    }

    if (typeof b.dataBase64 !== 'string' || b.dataBase64 === '') {
      return fail(422, 'VALIDATION_FAILED', 'The image bytes are missing.');
    }
    // Measured from the base64 rather than trusting a size the caller sent beside the bytes.
    const byteSize = decodedByteLength(b.dataBase64);
    if (byteSize === 0) return fail(422, 'VALIDATION_FAILED', 'That file is empty.');
    if (byteSize > EMAIL_IMAGE_MAX_BYTES) {
      return fail(
        422,
        'VALIDATION_FAILED',
        'That image is over the 2 MB limit. Compress it and try again.',
      );
    }

    const bytes = decodeBase64(b.dataBase64);
    if (bytes === null) return fail(422, 'VALIDATION_FAILED', 'Those bytes are not a valid upload.');
    const sniffed = sniffEmailImageMime(bytes);
    if (sniffed === 'refused') {
      return fail(
        422,
        'VALIDATION_FAILED',
        'That file type is not accepted. Upload a PNG, JPG, GIF or WebP.',
      );
    }
    const mime: EmailImageMime = sniffed ?? 'image/png';
    // Holds the sniffer's answer against the published allow-list, so the two cannot drift if
    // one of them ever grows without the other.
    if (!(EMAIL_IMAGE_MIMES as readonly string[]).includes(mime)) {
      return fail(
        422,
        'VALIDATION_FAILED',
        'That file type is not accepted. Upload a PNG, JPG, GIF or WebP.',
      );
    }

    const id = this.nextEmailId('e');
    const image: EmailImage = {
      id,
      mime,
      /*
       * The public byte-serve path, absolute, the way the real server builds it from its own
       * base.
       *
       * A `data:` URL was the other option and it was rejected: it would render in the console
       * and be refused outright by Gmail, Outlook.com and Yahoo, so a demo built on one would
       * teach an operator that something works which does not, and it would blow the
       * contract's 500 character bound on this field by four orders of magnitude. The cost is
       * that nothing in the offline demo serves these bytes, so an uploaded thumbnail will not
       * resolve. The value is right; there is just no server behind it.
       */
      url: `${MOCK_PUBLIC_BASE}/v1/email/images/${id}`,
      byteSize,
      // The real server reads these off the decoded image. The mock does not decode pixels, and
      // guessing would be worse than saying nothing: the contract makes both nullable for
      // exactly this case.
      width: null,
      height: null,
      label: typeof b.label === 'string' ? b.label.trim().slice(0, 120) : '',
      altText,
      uploadedByAdminId: adminId,
      createdAt: new Date().toISOString(),
    };
    this.emailImages.unshift(image);
    return ok(image, 201);
  }

  private deleteEmailImage(path: string): RawResponse {
    const id = decodeURIComponent(path.slice('/admin/email-images/'.length));
    const idx = this.emailImages.findIndex((image) => image.id === id);
    if (idx === -1) return fail(404, 'NOT_FOUND', 'No such image.');
    this.emailImages.splice(idx, 1);
    return ok(null, 204);
  }

  // ── broadcasts ──

  /**
   * The customers a selector reaches.
   *
   * `verified` and `unverified` are resolved against `kycStatus`, not against an email
   * verification flag: the mock's customer rows do not carry one. The contract means "email
   * verified", and this is the nearest fact the demo has. Said out loud here so nobody reads
   * the demo's counts as the API's.
   */
  private resolveAudience(audience: BroadcastAudience): AdminUserDetail[] {
    switch (audience.kind) {
      case 'users': {
        const wanted = new Set(audience.userIds);
        return this.users.filter((u) => wanted.has(u.id));
      }
      case 'all':
        return this.users.filter((u) => u.status === 'active');
      case 'verified':
        return this.users.filter((u) => u.kycStatus === 'verified');
      case 'unverified':
        return this.users.filter((u) => u.kycStatus !== 'verified');
      case 'tier':
        return this.users.filter((u) => u.tier === audience.tier);
      case 'signed_up_between': {
        const from = Date.parse(audience.from);
        const to = Date.parse(audience.to);
        return this.users.filter((u) => {
          const at = Date.parse(u.createdAt);
          return at >= from && at <= to;
        });
      }
    }
  }

  /** How many an audience reaches, how many would be mailed, and why the rest would not be. */
  private audienceCounts(audience: BroadcastAudience): BroadcastPreviewResponse {
    const rows = this.resolveAudience(audience);
    let noEmail = 0;
    let optedOut = 0;
    for (const u of rows) {
      if (u.email === null || u.email.trim() === '') {
        noEmail += 1;
        continue;
      }
      // Both broadcast categories honour the marketing preference. The contract is explicit
      // that `announcement` is for the operator's own record and is not a way to mark a
      // broadcast transactional, so the opt-out applies whichever one was chosen.
      if (!optedIntoMarketing(u.id)) optedOut += 1;
    }
    return {
      matched: rows.length,
      deliverable: rows.length - noEmail - optedOut,
      skipped: { noEmail, optedOut },
    };
  }

  private previewBroadcastAudience(body: unknown): RawResponse {
    const b = (body ?? {}) as { audience?: unknown; category?: unknown };
    const audience = readAudience(b.audience);
    if (audience === null) return fail(422, 'VALIDATION_FAILED', 'That audience is not one we know.');
    if (readCategory(b.category) === null) {
      return fail(422, 'VALIDATION_FAILED', 'A broadcast is either marketing or an announcement.');
    }
    return ok(this.audienceCounts(audience));
  }

  private createBroadcast(body: unknown, adminId: string): RawResponse {
    const b = (body ?? {}) as {
      subject?: unknown;
      preheader?: unknown;
      bodyHtml?: unknown;
      audience?: unknown;
      category?: unknown;
    };
    const subject = typeof b.subject === 'string' ? b.subject.trim() : '';
    if (subject === '') return fail(422, 'VALIDATION_FAILED', 'A broadcast needs a subject.');
    const bodyHtml = typeof b.bodyHtml === 'string' ? b.bodyHtml : '';
    if (bodyHtml.trim() === '') return fail(422, 'VALIDATION_FAILED', 'A broadcast needs a message.');
    const audience = readAudience(b.audience);
    if (audience === null) return fail(422, 'VALIDATION_FAILED', 'That audience is not one we know.');
    const category = readCategory(b.category);
    if (category === null) {
      return fail(422, 'VALIDATION_FAILED', 'A broadcast is either marketing or an announcement.');
    }

    const created: EmailBroadcastDetail = {
      id: this.nextEmailId('b'),
      subject: subject.slice(0, 255),
      preheader: typeof b.preheader === 'string' ? b.preheader.trim().slice(0, 200) : '',
      audience,
      category,
      status: 'draft',
      // Every count is zero, and creating resolves nothing. That is the whole reason creating
      // and sending are two calls: the audience is measured at the moment of sending, so a
      // draft written on Monday and sent on Friday reaches Friday's customers.
      totalRecipients: 0,
      sentCount: 0,
      failedCount: 0,
      skippedCount: 0,
      createdByAdminId: adminId,
      createdAt: new Date().toISOString(),
      startedAt: null,
      finishedAt: null,
      bodyHtml,
    };
    this.emailBroadcasts.unshift(created);
    return ok(created, 201);
  }

  /** The broadcast one path addresses, with an optional action suffix stripped off it. */
  private broadcastFrom(path: string, suffix = ''): EmailBroadcastDetail | undefined {
    const rest = path.slice('/admin/email-broadcasts/'.length);
    const raw = suffix !== '' && rest.endsWith(suffix) ? rest.slice(0, rest.length - suffix.length) : rest;
    const id = decodeURIComponent(raw);
    return this.emailBroadcasts.find((b) => b.id === id);
  }

  private getBroadcast(path: string): RawResponse {
    const found = this.broadcastFrom(path);
    if (found === undefined) return fail(404, 'NOT_FOUND', 'No such broadcast.');
    return ok(found);
  }

  private sendBroadcast(path: string): RawResponse {
    const found = this.broadcastFrom(path, '/send');
    if (found === undefined) return fail(404, 'NOT_FOUND', 'No such broadcast.');
    if (found.status !== 'draft') {
      return fail(409, 'CONFLICT', 'Only a draft can be sent. This one has already been queued.');
    }
    const counts = this.audienceCounts(found.audience);
    if (counts.deliverable === 0) {
      return fail(422, 'VALIDATION_FAILED', 'That audience reaches nobody right now.');
    }
    /*
     * The real path walks draft, queued, sending, completed, one state per worker tick. The
     * mock has no worker, so it collapses the walk into a single write and lands on the state
     * an operator would be looking at a few seconds later.
     *
     * `totalRecipients` is the deliverable count rather than the matched count, because the
     * recipient list is materialised at this moment and the skipped ones were never on it.
     */
    const startedAt = new Date().toISOString();
    const sent: EmailBroadcastDetail = {
      ...found,
      status: 'completed',
      totalRecipients: counts.deliverable,
      sentCount: counts.deliverable,
      skippedCount: counts.skipped.noEmail + counts.skipped.optedOut,
      startedAt,
      finishedAt: new Date().toISOString(),
    };
    this.emailBroadcasts[this.emailBroadcasts.indexOf(found)] = sent;
    return ok(sent);
  }

  private cancelBroadcast(path: string): RawResponse {
    const found = this.broadcastFrom(path, '/cancel');
    if (found === undefined) return fail(404, 'NOT_FOUND', 'No such broadcast.');
    if (found.status !== 'draft' && found.status !== 'queued' && found.status !== 'sending') {
      // Nothing left to stop. Mail already delivered stays delivered; there is no unsending,
      // and a cancel that pretended otherwise would be the most dangerous button on the page.
      return fail(409, 'CONFLICT', 'This broadcast has already finished.');
    }
    const cancelled: EmailBroadcastDetail = {
      ...found,
      status: 'cancelled',
      finishedAt: new Date().toISOString(),
    };
    this.emailBroadcasts[this.emailBroadcasts.indexOf(found)] = cancelled;
    // The list shape, matching what the client declares for this call: cancelling changes a
    // status, not the message, so there is no reason to send the body back with it.
    return ok(broadcastListShape(cancelled));
  }

  // ── proving the mail server ──

  /**
   * A verdict on a candidate SMTP configuration. Nothing is saved.
   *
   * The status codes matter here. 422 is for a body that is the wrong *shape*; everything
   * below that is a verdict about a configuration, and a verdict is a 200 carrying
   * `delivered: false`. The request was understood; the settings simply will not deliver, and
   * an operator reading a dialog needs the reason rather than a transport error.
   */
  private smtpTest(body: unknown): RawResponse {
    const b = (body ?? {}) as {
      host?: unknown;
      port?: unknown;
      username?: unknown;
      from?: unknown;
      to?: unknown;
      tlsMode?: unknown;
    };
    if (
      typeof b.host !== 'string' ||
      typeof b.port !== 'string' ||
      typeof b.from !== 'string' ||
      typeof b.to !== 'string'
    ) {
      return fail(
        422,
        'VALIDATION_FAILED',
        'A test needs a host, a port, a from address and a recipient.',
      );
    }
    const host = b.host.trim();
    const port = b.port.trim();
    const username = typeof b.username === 'string' ? b.username.trim() : '';
    const tlsMode = typeof b.tlsMode === 'string' ? b.tlsMode : '';

    if (host === '') {
      return ok(smtpVerdict('No mail server address was given.', null, false));
    }
    if (!/^\d{1,5}$/.test(port) || Number(port) < 1 || Number(port) > 65535) {
      return ok(smtpVerdict('The port must be a number between 1 and 65535.', null, false));
    }
    if (!EMAIL_RE.test(b.to.trim())) {
      return ok(smtpVerdict('That recipient is not a valid email address.', null, false));
    }
    if (!EMAIL_RE.test(b.from.trim())) {
      return ok(smtpVerdict('That from address is not a valid email address.', null, false));
    }
    if (tlsMode === 'none' && username !== '' && !isLoopbackHost(host)) {
      // Not retryable, because trying again unchanged sends the same password over the same
      // plaintext socket. The only fix is a different encryption mode.
      return ok(
        smtpVerdict(
          'A password cannot be sent to a remote server unencrypted. Use STARTTLS or TLS.',
          '530',
          false,
        ),
      );
    }
    const delivered: SmtpTestResponse = {
      delivered: true,
      message: null,
      code: '250',
      retryable: false,
    };
    return ok(delivered);
  }

  // ── push broadcast ──
  private broadcastPush(body: unknown, role: string): RawResponse {
    if (role !== 'superadmin') return fail(403, 'FORBIDDEN', 'Only a super admin can broadcast.');
    const b = (body ?? {}) as { title?: string; body?: string; category?: string };
    if (typeof b.title !== 'string' || b.title.trim() === '' || typeof b.body !== 'string' || b.body.trim() === '') {
      return fail(422, 'VALIDATION_FAILED', 'A broadcast needs a title and a body.');
    }
    // A plausible reach for the demo base, minus anyone who muted the category.
    const base = 4820;
    const muted = b.category === 'marketing' ? 640 : b.category === 'support' ? 90 : 0;
    return ok({ recipients: base - muted, category: b.category ?? 'marketing' });
  }

  /**
   * The binary read path — decode a stored base64 payload into a real Blob.
   *
   * Four kinds of bytes now come through here, and every one of them is bearer-scoped for the
   * same reason: a support attachment, a customer's face, an identity document and a service
   * icon are none of them public assets, so none of them can be an `<img src>` that works
   * without a session. The 401 comes first, before any path matching, so an expired token
   * cannot be told which of the four exists.
   */
  async sendMedia(req: RawRequest): Promise<RawMediaResponse> {
    await new Promise((r) => setTimeout(r, 160 + Math.random() * 160));
    const admin = this.adminFromToken(req.token);
    if (admin === null) {
      return mediaFail(401, 'UNAUTHENTICATED', 'Please sign in again.');
    }
    if (req.method !== 'GET') return mediaFail(404, 'NOT_FOUND', 'We could not find that.');

    if (req.path.startsWith('/admin/support/media/')) {
      const id = decodeURIComponent(req.path.slice('/admin/support/media/'.length));
      return mediaFrom(this.supportMedia[id], 'That attachment is no longer available.');
    }

    /*
     * A customer's photo.
     *
     * A customer with no photo answers 404, which is an empty state rather than a failure —
     * the avatar component treats it as "fall back to the lettermark" and never as an error
     * box, because a money screen does not show a broken image over a missing profile picture.
     */
    if (req.path.startsWith('/admin/users/') && req.path.endsWith('/photo')) {
      const id = decodeURIComponent(
        req.path.slice('/admin/users/'.length, req.path.length - '/photo'.length),
      );
      return mediaFrom(USER_PHOTOS[id], 'This customer has not set a profile photo.');
    }

    /*
     * The identity document. Gated on `kyc:read` here as the API will be: this is the single
     * most sensitive thing the console can render, and a role that cannot open the review
     * queue has no business fetching the scan behind it.
     */
    if (req.path.startsWith('/admin/kyc/queue/') && req.path.endsWith('/document')) {
      if (!adminCan(admin.role, 'kyc:read')) {
        return mediaFail(403, 'FORBIDDEN', 'Your role cannot view identity documents.');
      }
      const id = decodeURIComponent(
        req.path.slice('/admin/kyc/queue/'.length, req.path.length - '/document'.length),
      );
      return mediaFrom(KYC_DOCUMENTS[id], 'No document was filed with this submission.');
    }

    if (req.path.startsWith('/admin/bills/services/') && req.path.endsWith('/icon')) {
      const id = decodeURIComponent(
        req.path.slice('/admin/bills/services/'.length, req.path.length - '/icon'.length),
      );
      return mediaFrom(BILL_ICONS[id], 'This service has no uploaded icon.');
    }

    return mediaFail(404, 'NOT_FOUND', 'We could not find that.');
  }
}

/** Whether a patch bailed out with a response instead of returning a record. */
function isRawResponse(value: AdminUserDetail | RawResponse): value is RawResponse {
  return typeof (value as { ok?: unknown }).ok === 'boolean';
}

/** A typed error envelope on the binary path, shaped exactly like the JSON one. */
function mediaFail(status: number, code: string, message: string): RawMediaResponse {
  return {
    status,
    ok: false,
    blob: null,
    name: null,
    json: { error: { code, message, requestId: 'mock-req' } },
  };
}

/** A stored payload as a real Blob, or a 404 that says which thing was missing. */
function mediaFrom(
  media: { mime: string; name: string; dataBase64: string } | undefined,
  missing: string,
): RawMediaResponse {
  if (media === undefined) return mediaFail(404, 'NOT_FOUND', missing);
  const blob = new Blob([base64ToArrayBuffer(media.dataBase64)], { type: media.mime });
  return { status: 200, ok: true, blob, name: media.name, json: null };
}

/**
 * What an uploaded icon's bytes actually are.
 *
 * Its own sniffer rather than a call to `sniffSplashMime` or `sniffEmailImageMime`, for the
 * reason the contract gives for keeping each module's sniffer separate: they accept different
 * sets (a splash may be MP4, a mail image may be GIF, an icon may be neither), and sharing one
 * would mean widening a set for one caller silently widens it for the others.
 */
function sniffIconMime(bytes: Uint8Array): string | null {
  if (bytes.length >= 8 && bytes[0] === 0x89 && ascii(bytes, 1, 4) === 'PNG') return 'image/png';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') {
    return 'image/webp';
  }
  return null;
}

/**
 * Turn an operator's typed price into an exact rational in lowest terms.
 *
 * This mirrors `Rate.fromDecimal` in `@genex/money`, because in demo mode this file *is*
 * the API and the parse has to happen server-side of the seam. It is deliberately not in
 * `lib/rate.ts`: the console proper must never do this arithmetic, only pass the characters
 * through. Pure string surgery over bigints — strip the point, count the fraction digits,
 * raise ten to that power — so no float ever holds the price.
 */
function toRational(price: string): { numerator: string; denominator: string } | null {
  const trimmed = price.trim();
  if (!/^(?:0|[1-9]\d*)(?:\.\d{1,8})?$/.test(trimmed) || !/[1-9]/.test(trimmed)) return null;
  const dot = trimmed.indexOf('.');
  const whole = dot === -1 ? trimmed : trimmed.slice(0, dot);
  const frac = dot === -1 ? '' : trimmed.slice(dot + 1);
  const numerator = BigInt(whole + frac);
  const denominator = 10n ** BigInt(frac.length);
  const divisor = gcd(numerator, denominator);
  return { numerator: (numerator / divisor).toString(), denominator: (denominator / divisor).toString() };
}

function gcd(a: bigint, b: bigint): bigint {
  let x = a;
  let y = b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x === 0n ? 1n : x;
}

/**
 * A typed gift-card rate → the exact rational the catalogue stores: **kobo** per one whole
 * unit of face value.
 *
 * `toRational` above gives naira per unit; the extra step is the ×100 into kobo, and it is
 * the whole reason this is a separate function rather than a call to that one. Getting it
 * wrong is a silent hundred-fold error in every payout the brand ever pays, which is exactly
 * the sort of thing a demo backend must not be able to disagree with the API about.
 */
function giftcardRational(price: string): { numerator: string; denominator: string } | null {
  const naira = toRational(price);
  if (naira === null) return null;
  const numerator = BigInt(naira.numerator) * 100n;
  const denominator = BigInt(naira.denominator);
  const divisor = gcd(numerator, denominator);
  return { numerator: (numerator / divisor).toString(), denominator: (denominator / divisor).toString() };
}

const GIFTCARD_CATEGORIES = ['shopping', 'entertainment', 'gaming', 'other'];
const GIFTCARD_FACE_CURRENCIES = ['USD', 'GBP', 'EUR', 'CAD', 'AUD'];

/**
 * Every field-level refusal the brand routes make, in one place so create and patch cannot
 * drift apart — the same reason the API keeps `assertFaceBounds` in one function.
 *
 * `required` distinguishes the two calls: a create must carry every field, a patch may carry
 * any subset. The values passed in are always the *merged* ones for a patch, because the API
 * validates the result of the edit rather than the edit itself.
 */
function validateBrandFields(
  patch: { readonly rateNairaPerUnit?: string | undefined },
  merged: {
    name?: string | undefined;
    category?: string | undefined;
    country?: string | undefined;
    faceCurrency?: string | undefined;
    rate?: string | undefined;
    minFace?: { currency: string; amount: string } | undefined;
    maxFace?: { currency: string; amount: string } | undefined;
    sortOrder?: number | undefined;
    required: boolean;
  },
): RawResponse | null {
  const need = merged.required;
  if (need || merged.name !== undefined) {
    const name = (merged.name ?? '').trim();
    if (name === '' || name.length > 80) return fail(422, 'VALIDATION_FAILED', 'name: a brand needs a name of at most 80 characters');
  }
  if (need || merged.category !== undefined) {
    if (!GIFTCARD_CATEGORIES.includes(merged.category ?? '')) {
      return fail(422, 'VALIDATION_FAILED', `category: must be one of ${GIFTCARD_CATEGORIES.join(', ')}`);
    }
  }
  if (need || merged.country !== undefined) {
    if (!/^[A-Z]{2}$/.test(merged.country ?? '')) {
      return fail(422, 'VALIDATION_FAILED', 'country: must be an ISO-3166-1 alpha-2 code');
    }
  }
  const faceCurrency = merged.faceCurrency ?? '';
  if (need || merged.faceCurrency !== undefined) {
    if (!GIFTCARD_FACE_CURRENCIES.includes(faceCurrency)) {
      return fail(422, 'VALIDATION_FAILED', `faceCurrency: must be one of ${GIFTCARD_FACE_CURRENCIES.join(', ')}`);
    }
  }
  // The rate's *shape* is checked whenever one is in the patch; the deviation guard is the
  // caller's, because only an edit has a standing rate to be measured against.
  if (need || patch.rateNairaPerUnit !== undefined) {
    const rate = merged.rate ?? '';
    if (!/^(?:0|[1-9]\d*)(?:\.\d{1,8})?$/.test(rate.trim())) {
      return fail(
        422,
        'VALIDATION_FAILED',
        'rateNairaPerUnit: must be a plain decimal rate in naira per face unit, e.g. 1200.50',
      );
    }
    if (!/[1-9]/.test(rate)) {
      return fail(422, 'VALIDATION_FAILED', 'rateNairaPerUnit: rate must be greater than zero');
    }
  }
  for (const [label, face] of [['minFace', merged.minFace], ['maxFace', merged.maxFace]] as const) {
    if (!need && face === undefined) continue;
    if (face === undefined || !/^[1-9]\d*$/.test(face.amount)) {
      return fail(422, 'VALIDATION_FAILED', `${label}.amount: must be greater than zero`);
    }
    if (face.currency !== faceCurrency) {
      return fail(422, 'VALIDATION_FAILED', `${label}.currency: must be the brand’s face currency (${faceCurrency})`);
    }
  }
  if (merged.minFace !== undefined && merged.maxFace !== undefined) {
    if (BigInt(merged.minFace.amount) > BigInt(merged.maxFace.amount)) {
      return fail(422, 'VALIDATION_FAILED', 'maxFace.amount: the maximum face value cannot be below the minimum');
    }
  }
  if (merged.sortOrder !== undefined) {
    if (!Number.isInteger(merged.sortOrder) || merged.sortOrder < 0 || merged.sortOrder > 10_000) {
      return fail(422, 'VALIDATION_FAILED', 'sortOrder: must be a whole number between 0 and 10000');
    }
  }
  return null;
}

// ── Splash validation (mirrors `modules/admin/brand.ts`) ────────────────────────────

/** `SPLASH_ASSET_MIMES` — the raster set plus MP4. No SVG: it is an active document. */
const SPLASH_MIMES: readonly string[] = ['image/png', 'image/jpeg', 'image/webp', 'video/mp4'];

/** `SPLASH_ASSET_MAX_BYTES`, decoded. Six megabytes; the API's comment carries the arithmetic. */
const SPLASH_MAX_BYTES = 6_000_000;

/** `KYC_MANUAL_REVIEW_SLA_SECONDS` — a day, as the contract publishes it. */
const KYC_SLA_SECONDS = 24 * 60 * 60;

/** The `ftyp` major brands the API accepts as "an MP4 a phone can play". */
const MP4_BRANDS: readonly string[] = [
  'isom', 'iso2', 'iso4', 'iso5', 'iso6', 'mp41', 'mp42', 'mp4v', 'avc1', 'M4V ', 'dash',
];

/** Decode base64 to bytes, or null when it is not base64 at all. */
function decodeBase64(value: string): Uint8Array | null {
  try {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

function ascii(bytes: Uint8Array, from: number, to: number): string {
  let out = '';
  for (let i = from; i < to && i < bytes.length; i += 1) out += String.fromCharCode(bytes[i]!);
  return out;
}

/**
 * The magic-number sniff, byte for byte the API's `sniffSplashMime`.
 *
 * This is the check that catches a payload lying about what it is, and it matters on a route
 * whose bytes are later served publicly under the stored `Content-Type`. `heic`/`avif` also
 * begin with `ftyp` and are *images*, so the brand list is an allowlist rather than a
 * permissive "starts with ftyp" test — the purpose is to confirm the declared type, and a
 * guess is not a confirmation.
 */
function sniffSplashMime(bytes: Uint8Array): SplashAssetMime | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 &&
    bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a
  ) {
    return 'image/png';
  }
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') return 'image/webp';
  if (bytes.length >= 12 && ascii(bytes, 4, 8) === 'ftyp' && MP4_BRANDS.includes(ascii(bytes, 8, 12))) {
    return 'video/mp4';
  }
  return null;
}

/**
 * A PNG's intrinsic size: the IHDR chunk is mandatory and must come first, so width and
 * height are always big-endian `uint32`s at bytes 16 and 20. The one dimension read this
 * mock does — see `uploadSplash` for why JPEG and WebP are left to the real server.
 */
function pngDimensions(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes.length < 24 || ascii(bytes, 12, 16) !== 'IHDR') return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const width = view.getUint32(16);
  const height = view.getUint32(20);
  return width > 0 && height > 0 ? { width, height } : null;
}

/** In the mock, a logo asset resolves to the support-media-style route; a neutral placeholder otherwise. */
const API_MEDIA_LOGO = 'https://dummyimage.com/160x34/5b2ce6/ffffff.png&text=GenEx';

/** Decode standard base64 into an ArrayBuffer, for turning a stored attachment into a Blob. */
function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
  return buffer;
}

// ── The email platform's own helpers ───────────────────────────────────────────

/**
 * Good enough to tell an address from a typo, and deliberately no stricter.
 *
 * The real validator is the mail server, which either accepts the recipient or does not. A
 * regular expression that tries to encode RFC 5322 rejects real addresses, and the ones it
 * rejects belong to the people least able to work around it.
 */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * The public base a stored image URL is built from.
 *
 * The real server composes this from its own configured address, which is why the console
 * never builds one and why `EmailImage.url` is nullable: a deployment with no public address
 * stores the image and admits it cannot be referenced.
 */
const MOCK_PUBLIC_BASE = 'https://api.genex.pay';

/**
 * Uppercase, matching `hexColorSchema` exactly.
 *
 * Lowercase is refused rather than quietly upper-cased, and that is the point: a browser's
 * colour input hands back `#ffffff`, so a console that forwards it untouched will be refused
 * by the API. A mock that accepted it would hide that until production. `normaliseHex` in
 * `lib/bannerContrast.ts` is the fix, and it already exists.
 */
const HEX_COLOR = /^#[0-9A-F]{6}$/;

/** Mirrors `BROADCAST_MAX_NAMED_USERS`: how many customers one broadcast may name explicitly. */
const BROADCAST_MAX_NAMED_USERS = 500;

const TOKEN_RE = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;

/**
 * Fill `{{token}}` placeholders, recording every one nothing could fill.
 *
 * An unfilled token is left visible rather than blanked. A blank is indistinguishable from a
 * value that happened to be empty, and the whole reason `missingVariables` exists is that
 * `{{reference}}` reaching a customer's inbox is the failure nobody catches in review.
 */
function substitute(source: string, vars: Record<string, string>, missing: Set<string>): string {
  return source.replace(TOKEN_RE, (whole: string, name: string) => {
    const value = vars[name];
    if (value === undefined) {
      missing.add(name);
      return whole;
    }
    return value;
  });
}

/**
 * The plain-text alternative, assembled from the parts rather than scraped off the document.
 *
 * Every message goes out `multipart/alternative`, and the text leg is what a plain-text client
 * shows, what several spam filters score, and what a screen reader may prefer. Footer links
 * appear as `label: address` because a text part that says "Terms" with no address is worse
 * than useless.
 */
function emailPlainText(
  layout: EmailLayoutContent,
  brand: { supportEmail: string },
  preheader: string,
  bodyHtml: string,
): string {
  const lines: string[] = [];
  const pre = preheader.trim();
  if (pre !== '') lines.push(pre, '');
  lines.push(htmlToPlainText(bodyHtml).trim(), '');
  lines.push('---');

  const footerText = layout.footerText.trim();
  if (footerText !== '') lines.push(footerText);
  const support = brand.supportEmail.trim();
  if (support !== '') lines.push(`Need help? Contact ${support}`);
  for (const link of [...layout.socialLinks, ...layout.legalLinks]) {
    lines.push(`${link.label}: ${link.url}`);
  }
  const legal = layout.footerLegalText.trim();
  if (legal !== '') lines.push(legal);

  return lines.join('\n');
}

/**
 * The sample the layout preview is drawn around.
 *
 * Short, and painted entirely with layout tokens, so what an operator judges is the chrome and
 * the palette rather than one template's copy.
 */
const LAYOUT_PREVIEW_MESSAGE = {
  subject: 'A sample message from {{platformName}}',
  preheader: 'This is what your layout looks like.',
  html:
    '<h1 style="margin:0 0 16px 0;font-size:22px;line-height:30px;font-weight:700;color:{{layoutHeading}};">A sample message</h1>' +
    '<p style="margin:0 0 16px 0;font-size:15px;line-height:24px;color:{{layoutBody}};">This is body text on the card, at the size a real message uses. Links look like <a href="{{websiteUrl}}" style="color:{{layoutLink}};">this one</a>.</p>' +
    '<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 20px 0;"><tr><td align="center" bgcolor="{{layoutButtonBg}}" style="background-color:{{layoutButtonBg}};border-radius:10px;"><a href="{{websiteUrl}}" style="display:inline-block;padding:14px 28px;font-size:15px;font-weight:600;color:{{layoutButtonText}};text-decoration:none;border-radius:10px;">A button</a></td></tr></table>' +
    '<p style="margin:16px 0 0 0;font-size:13px;line-height:20px;color:{{layoutMuted}};">And this is the small print, which is the line that most often turns out to be unreadable.</p>',
  usesLayout: true,
};

/** A failed SMTP verdict. Not an error: the request worked, the configuration did not. */
function smtpVerdict(message: string, code: string | null, retryable: boolean): SmtpTestResponse {
  return { delivered: false, message, code, retryable };
}

/**
 * Whether a host is this machine.
 *
 * Sending a password unencrypted to a server on the same box never crosses a network, so the
 * refusal that applies to a remote host would be theatre there. Local development mail
 * catchers are the reason this exception exists at all.
 */
function isLoopbackHost(host: string): boolean {
  const value = host.trim().toLowerCase();
  return (
    value === 'localhost' ||
    value === '127.0.0.1' ||
    value === '::1' ||
    value === '[::1]' ||
    value.endsWith('.localhost')
  );
}

/**
 * Whether a demo customer has opted into marketing.
 *
 * Derived from the id rather than randomised, so the count on the composer is the same on
 * every reload. A marketing audience that changes size between two glances is a demo that
 * teaches an operator to distrust the number, which is the one thing this page cannot afford.
 */
function optedIntoMarketing(id: string): boolean {
  let sum = 0;
  for (let i = 0; i < id.length; i += 1) sum += id.charCodeAt(i);
  return sum % 4 !== 0;
}

/** The list shape: the stored row minus its body, which the list deliberately omits. */
function broadcastListShape(broadcast: EmailBroadcastDetail): EmailBroadcast {
  const { bodyHtml: _bodyHtml, ...rest } = broadcast;
  return rest;
}

/** The decoded size of a base64 payload, without decoding it. */
function decodedByteLength(base64: string): number {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

/**
 * The real type of an upload, from its first bytes.
 *
 * A magic number is evidence; a content type the caller sent alongside is a claim, which is
 * why the contract does not ask for one.
 *
 * Returns `'refused'` for the formats that are recognisably *not* one of the four, SVG above
 * all: it is a document format that can carry script, mail clients disagree about which parts
 * of it they strip, and nothing a banner needs requires it. Returns null when the bytes match
 * nothing at all, which the caller treats as a PNG rather than a refusal so a hand-typed demo
 * payload still lands somewhere sensible.
 */
function sniffEmailImageMime(bytes: Uint8Array): EmailImageMime | 'refused' | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 &&
    bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a
  ) {
    return 'image/png';
  }
  if (bytes.length >= 6 && ascii(bytes, 0, 4) === 'GIF8') return 'image/gif';
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') {
    return 'image/webp';
  }
  // A zip container: an Office document, or anything else wearing one.
  if (bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b) return 'refused';
  const head = ascii(bytes, 0, 16).trimStart().toLowerCase();
  if (
    head.startsWith('%pdf') ||
    head.startsWith('<svg') ||
    head.startsWith('<?xml') ||
    head.startsWith('<!doctype') ||
    head.startsWith('<html')
  ) {
    return 'refused';
  }
  return null;
}

/** A parsed selector, or null when it is not one this platform knows how to address. */
function readAudience(value: unknown): BroadcastAudience | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as {
    kind?: unknown;
    userIds?: unknown;
    tier?: unknown;
    from?: unknown;
    to?: unknown;
  };
  switch (raw.kind) {
    case 'users': {
      if (!Array.isArray(raw.userIds)) return null;
      const given: readonly unknown[] = raw.userIds;
      const ids = given.filter((id): id is string => typeof id === 'string' && id !== '');
      if (ids.length === 0 || ids.length !== given.length) return null;
      if (ids.length > BROADCAST_MAX_NAMED_USERS) return null;
      return { kind: 'users', userIds: ids };
    }
    case 'all':
      return { kind: 'all' };
    case 'verified':
      return { kind: 'verified' };
    case 'unverified':
      return { kind: 'unverified' };
    case 'tier': {
      // Held against the real tier list rather than merely bounded as a string, which is the
      // job the contract explicitly leaves to the service.
      if (typeof raw.tier !== 'string') return null;
      if (!(KYC_TIERS as readonly string[]).includes(raw.tier)) return null;
      return { kind: 'tier', tier: raw.tier };
    }
    case 'signed_up_between': {
      if (typeof raw.from !== 'string' || typeof raw.to !== 'string') return null;
      const from = Date.parse(raw.from);
      const to = Date.parse(raw.to);
      if (Number.isNaN(from) || Number.isNaN(to)) return null;
      // A range typed backwards selects nobody, and quietly returning nobody is how an
      // operator concludes the audience is empty rather than that they swapped two dates.
      if (from > to) return null;
      return { kind: 'signed_up_between', from: raw.from, to: raw.to };
    }
    default:
      return null;
  }
}

/**
 * The category, defaulting to marketing when absent, as the contract's schema does.
 *
 * Both values honour the customer's unsubscribe. The distinction is for the operator's own
 * record and for the footer copy, and deliberately not a way to mark a broadcast
 * transactional: that door is the one every "just this once" request walks through.
 */
function readCategory(value: unknown): BroadcastCategory | null {
  if (value === undefined || value === null) return 'marketing';
  if (typeof value !== 'string') return null;
  return BROADCAST_CATEGORIES.find((category) => category === value) ?? null;
}

/** A set of footer or social links, or the reason it was refused. */
function readEmailLinks(
  value: unknown,
  max: number,
  what: string,
): { readonly links: EmailLink[] } | { readonly error: string } {
  if (value === undefined || value === null) return { links: [] };
  if (!Array.isArray(value)) return { error: `The ${what} must be a list.` };
  const given: readonly unknown[] = value;
  const rows: { label: string; url: string }[] = [];
  for (const entry of given) {
    if (typeof entry !== 'object' || entry === null) {
      return { error: `Every one of the ${what} needs a label and an address.` };
    }
    const row = entry as { label?: unknown; url?: unknown };
    if (typeof row.label !== 'string' || typeof row.url !== 'string') {
      return { error: `Every one of the ${what} needs a label and an address.` };
    }
    rows.push({ label: row.label, url: row.url });
  }
  // `normaliseEmailLinks` is the console's mirror of `emailLinkUrlSchema`: https only, no
  // credentials in the authority, label bounded at 40. Reused rather than restated, so an
  // address the form refuses is refused here too.
  const result = normaliseEmailLinks(rows, max, what);
  if ('errors' in result) return { error: result.errors.join(' ') };
  return { links: [...result.links] };
}

/**
 * A whole layout content block, validated by hand against the contract's rules.
 *
 * By hand because this console bundles `@genex/contracts` for types only, so the schema itself
 * is not available at runtime here. Every bound below is the contract's, and the palette rule
 * at the end is the console's own mirror of it rather than a fourth copy.
 */
function readEmailLayoutContent(
  body: unknown,
): { readonly content: EmailLayoutContent } | { readonly error: string } {
  if (typeof body !== 'object' || body === null) return { error: 'Send the whole layout.' };
  const raw = body as Record<string, unknown>;

  const senderNameRaw = raw.senderName;
  if (typeof senderNameRaw !== 'string' || senderNameRaw.trim().length > 120) {
    return { error: 'The sender name must be text of at most 120 characters.' };
  }
  const senderName = senderNameRaw.trim();

  // Null leaves the reply-to header off entirely rather than sending an empty one, so a blank
  // string is read as "no reply-to" rather than refused.
  const replyToRaw = raw.replyTo;
  let replyTo: string | null = null;
  if (typeof replyToRaw === 'string' && replyToRaw.trim() !== '') {
    const candidate = replyToRaw.trim();
    if (!EMAIL_RE.test(candidate) || candidate.length > 254) {
      return { error: 'The reply-to address is not a valid email address.' };
    }
    replyTo = candidate;
  } else if (replyToRaw !== null && replyToRaw !== undefined && typeof replyToRaw !== 'string') {
    return { error: 'The reply-to address is not a valid email address.' };
  }

  const showLogo = raw.showLogo;
  if (typeof showLogo !== 'boolean') return { error: 'Say whether the logo is shown.' };

  const logoWidthPx = raw.logoWidthPx;
  if (
    typeof logoWidthPx !== 'number' ||
    !Number.isInteger(logoWidthPx) ||
    logoWidthPx < EMAIL_LOGO_MIN_WIDTH ||
    logoWidthPx > EMAIL_LOGO_MAX_WIDTH
  ) {
    return {
      error: `The logo width must be a whole number between ${String(EMAIL_LOGO_MIN_WIDTH)} and ${String(EMAIL_LOGO_MAX_WIDTH)} pixels.`,
    };
  }

  /*
   * Every colour is read into a complete record before the legibility rule runs, because that
   * rule is about *pairs* and a half-filled palette cannot be judged, only refused field by
   * field.
   *
   * The empty strings below are placeholders that never survive: the loop covers all eleven
   * fields and returns on the first one that is missing or malformed, so by the time the
   * record is used every slot has been written.
   */
  const palette: Record<EmailPaletteField, string> = {
    pageBackground: '',
    cardBackground: '',
    headerBackground: '',
    headingColor: '',
    bodyTextColor: '',
    mutedTextColor: '',
    linkColor: '',
    buttonBackground: '',
    buttonTextColor: '',
    footerBackground: '',
    footerTextColor: '',
  };
  for (const { field, label } of PALETTE_FIELDS) {
    const value = raw[field];
    if (typeof value !== 'string' || !HEX_COLOR.test(value)) {
      return { error: `${label}: use an uppercase six digit hex colour like #101828.` };
    }
    palette[field] = value;
  }

  const footerTextRaw = raw.footerText;
  if (typeof footerTextRaw !== 'string' || footerTextRaw.trim().length > 600) {
    return { error: 'The footer text must be at most 600 characters.' };
  }
  const footerLegalTextRaw = raw.footerLegalText;
  if (typeof footerLegalTextRaw !== 'string' || footerLegalTextRaw.trim().length > 600) {
    return { error: 'The legal line must be at most 600 characters.' };
  }

  const social = readEmailLinks(raw.socialLinks, 8, 'social links');
  if ('error' in social) return social;
  const legal = readEmailLinks(raw.legalLinks, 6, 'legal links');
  if ('error' in legal) return legal;

  const content: EmailLayoutContent = {
    senderName,
    replyTo,
    showLogo,
    logoWidthPx,
    ...palette,
    footerText: footerTextRaw.trim(),
    footerLegalText: footerLegalTextRaw.trim(),
    socialLinks: social.links,
    legalLinks: legal.links,
  };

  /*
   * The palette rule, last, because it is the only check that judges the layout as a whole.
   *
   * An operator picking colours in a form cannot see a contrast ratio, and email has no dark
   * mode to rescue a bad pair: the document declares `color-scheme: light` precisely so clients
   * stop inverting it, which means whatever is configured here is what every reader sees. Every
   * failing pair is reported rather than the first, so somebody who used one bad colour twice
   * fixes it once.
   */
  const verdict = checkEmailPalette(content);
  if (!verdict.ok) {
    return { error: verdict.failures.map((failure) => failure.message).join(' ') };
  }
  return { content };
}
